/* GENERATED by _app/scripts/build-site.mjs — do not edit, do not commit. */
import React from 'react';
import * as ReactDOMLegacy from 'react-dom';
import * as ReactDOMClient from 'react-dom/client';
const ReactDOM = Object.assign({}, ReactDOMLegacy, ReactDOMClient);

/* ======== website/primitives.jsx ======== */
/* Jerk Alert — UI primitives */

// Build a Lucide SVG string from icon name (kebab-case)
function jaIconSvg(name, size, sw) {
  const L = window.lucide;
  const pascal = name.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
  let node = L && L.icons && (L.icons[pascal] || L.icons[name]);
  // The app ships a hand-listed icon set (_app/src/lib/icons.js). A name that is not in it
  // used to render an empty box in silence; say so instead.
  if (!node) { console.warn('lucide icon missing:', name, '— add it to _app/src/lib/icons.js'); return ''; }
  // Lucide ships two node shapes: the CDN global gives a flat list of children,
  // the npm package gives a whole ['svg', attrs, children] node. Normalize to children.
  if (Array.isArray(node) && typeof node[0] === 'string') node = Array.isArray(node[2]) ? node[2] : [];
  if (!Array.isArray(node)) return '';
  const kids = node.map(child => {
    let tag, attrs;
    if (Array.isArray(child)) { tag = child[0]; attrs = child[1] || {}; }
    else if (child && typeof child === 'object') { tag = child.tag || child.name; attrs = child.attrs || child.attributes || child.props || {}; }
    if (!tag) return '';
    const a = Object.entries(attrs)
      .filter(([k, v]) => k !== 'children' && v != null && typeof v !== 'object')
      .map(([k, v]) => `${k}="${v}"`).join(' ');
    return `<${tag} ${a}/>`;
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round">${kids}</svg>`;
}

function Icon({ name, size = 20, sw = 2, color, style, className }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current) return;
    try { ref.current.innerHTML = jaIconSvg(name, size, sw); }
    catch (e) { ref.current.innerHTML = ''; console.warn('icon failed:', name, e); }
  }, [name, size, sw]);
  return <span ref={ref} className={'ico ' + (className || '')} style={{ width: size, height: size, color, ...style }} />;
}

function Button({ variant = 'primary', size = 'md', icon, children, ...rest }) {
  return (
    <button className={`btn btn-${variant} btn-${size}`} {...rest}>
      {icon && <Icon name={icon} size={size === 'lg' ? 19 : 17} />}
      {children}
    </button>
  );
}

// Plate + state entry. Every sheet that asks for a plate uses these two, so the keyboard hints,
// the 8-character cap and the state list are decided in one place.
// PlateEntry (SitePlateDisplay.jsx) lifts the cap to 10 while Personalized is on: 8 characters + 2 separators.
function PlateInput({ value, onChange, placeholder = 'ABC123', style, maxLength = 8 }) {
  return <input className="field plate-field" value={value} placeholder={placeholder} maxLength={maxLength}
    autoCapitalize="characters" autoCorrect="off" spellCheck={false} style={style}
    onChange={e => onChange(e.target.value.toUpperCase())} />;
}
function StateSelect({ value, onChange, style }) {
  return (
    <select className="field state-select" value={value} aria-label="State" style={{ width:88, ...style }} onChange={e => onChange(e.target.value)}>
      <option value="">ST</option>
      {(window.JA_STATES || []).map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

function Badge({ kind, icon, children }) {
  return <span className={`badge badge-${kind}`}>{icon && <Icon name={icon} size={13} />}{children}</span>;
}

function StatusBadge({ status }) {
  const map = {
    offender: { kind: 'offender', icon: 'siren', label: 'REPEAT OFFENDER' },
    cleared:  { kind: 'cleared', icon: 'shield-check', label: 'CLEARED' },
    trending: { kind: 'trending', icon: 'trending-up', label: 'TRENDING ALERT' },
    verified: { kind: 'verified', icon: 'hard-hat', label: 'VERIFIED LOOKOUT' },
  };
  const m = map[status];
  if (!m) return null;
  return <Badge kind={m.kind} icon={m.icon}>{m.label}</Badge>;
}

const AV_COLORS = ['#15677D', '#F97316', '#DD3A0B', '#18A06A', '#F5A623', '#0E4D5E'];
function Avatar({ name, init, size = 40 }) {
  const idx = (init ? init.charCodeAt(0) : 0) % AV_COLORS.length;
  return <span className="avatar" style={{ width: size, height: size, background: AV_COLORS[idx], fontSize: size * 0.38 }}>{init}</span>;
}

// Erk SCORE colour (0.0 best → 5.0 worst). Bands follow jerkTitle() so colour and title always
// change together (owner, 1 Oct 2026): Road Saint / Road Scholar / Friend of the Road = green,
// Typical / Needs Work = yellow, Confirmed Erk'r = red-orange then red from 4.5, Certified Jerk = red.
function jerkColor(score) {
  const s = Math.round((score || 0) * 10) / 10;
  if (s < 2)   return 'var(--awesome)';
  if (s < 4)   return 'var(--jerk-1)';
  if (s < 4.5) return 'var(--jerk-4)';
  return 'var(--jerk-5)';
}
// Alert SEVERITY colour (the 1–5 a reporter picks on one alert). This is the original yellow→red
// ladder: a single level-1 alert is still an alert and must not read green.
function levelColor(level) {
  const v = ['var(--jerk-1)','var(--jerk-1)','var(--jerk-2)','var(--jerk-3)','var(--jerk-4)','var(--jerk-5)'];
  return v[Math.round(level)] || 'var(--jerk-3)';
}
// Rating title by Erk score band (0.0 best → 5.0 worst)
function jerkTitle(level) {
  // Erk score bands. Scores are rounded to one decimal, so 4.6 is the first Certified Jerk.
  if (level === 0)    return 'Road Saint';   // was 'Ubermensch' — see note in LAUNCH-CHECKLIST; one-word revert
  if (level < 1)      return 'Road Scholar';
  if (level < 2)      return 'Friend of the Road';
  if (level < 3)      return 'Typical';
  if (level < 4)      return 'Needs Work';
  if (level < 4.55)   return 'Confirmed Erk’r';
  return 'Certified Jerk';
}
// One triangle of the meter, lit from the left in quarter steps. The track-coloured outline is
// always drawn; the score colour paints over it through a clipping box, so 0.8 shows three
// quarters of the first triangle rather than nothing at all.
function MeterTri({ fill, color, size }) {
  const q = Math.floor(Math.min(1, Math.max(0, fill)) * 4) / 4;
  return (
    <span style={{ position:'relative', display:'inline-flex', width:size, height:size, flex:'none' }}>
      <Icon name="triangle-alert" size={size} sw={2.2} style={{ color:'var(--jerk-track)' }} />
      {q > 0 && (
        <span style={{ position:'absolute', top:0, left:0, bottom:0, width:(q*100)+'%', overflow:'hidden', display:'flex' }}>
          <Icon name="triangle-alert" size={size} sw={2.2} style={{ color, flex:'none' }} />
        </span>
      )}
    </span>
  );
}
function JerkMeter({ level, size = 20, showScore = false, count }) {
  // Round ONCE, to the same tenth the printed score shows, so triangles, title and number agree.
  const score = Math.round((level || 0) * 10) / 10;
  const color = jerkColor(score);
  return (
    <span className="flex center gap-2" style={{ flexWrap:'wrap', rowGap:2 }}>
      <span className="meter">
        {[0,1,2,3,4].map(i => <MeterTri key={i} fill={score - i} color={color} size={size} />)}
      </span>
      {showScore && <span style={{ fontFamily:'var(--font-display)', fontWeight:900, fontSize: size*1.0, color }}>{score.toFixed(1)}</span>}
      {count != null && <span className="ja-meta" style={{ fontFamily:'var(--font-body)', fontWeight:600, fontSize:12.5, color:'var(--fg3)', letterSpacing:'.02em', whiteSpace:'nowrap' }}>· {count} ALERT{count === 1 ? '' : 'S'}</span>}
    </span>
  );
}

function Logo({ size = 24, onClick }) {
  return (
    <div className="logo" onClick={onClick}>
      <img src="assets/logo-mark.webp" alt="Jerk Alert" width="512" height="512" />
      <span className="logo-word" style={{ fontSize: size }}>
        <span style={{ color:'var(--ja-ink)' }}>jerk</span>
        <span style={{ color:'var(--ja-alert)' }}>alert</span>
        <span style={{ color:'var(--fg3)', fontSize: size*0.62, fontWeight:800 }}>.org</span>
      </span>
    </div>
  );
}

// Map a reason label to its illustrated infraction icon (sliced sheet). Null if none.
const INF_LABELS = ['Road Raging','Tailgating','Speeding','Brake Checking','DUI / DWI','Reckless Driving','Gesturing / Swearing','Threatening to Fight','Aggressive Honking','High Beams','Slowing Down Intentionally','Sus','Left Lane Squatter','Running Red Lights','Ignoring Stop Signs','Threatening Pedestrians & Cyclists','Right-of-Way Challenged','Improper Lane Changes','Merging Dangerously','Failure to Signal','Cutting Off Other Drivers','Distracted Driving','Texting','Blocked / Took a Parking Spot','Blocking Handicapped Access'];
// Illustrated "Friendly Heads Up" stickers
const HEADS_SRC = {
  'Thank You':'icons/heads-thank-you.webp',
  'Excellent Driving!':'icons/heads-excellent.webp',
  'Love the Style':'icons/heads-love-style.webp',
  'Expired Tags (Quarterly Reset)':'icons/heads-expired-tags.webp',
  'Broken Light (Quarterly Reset)':'icons/heads-broken-light.webp',
  // Awards
  'Avoided Accident':'icons/award-avoided-accident.webp',
  'Cat-like Reflexes':'icons/award-cat-reflexes.webp',
  'Defensive Driving Champion':'icons/award-defensive-champion.webp',
  'Road Ninja':'icons/award-road-ninja.webp',
  'Better Late Than Never':'icons/award-better-late.webp',
};
function infIconSrc(reason) {
  if (HEADS_SRC[reason]) return HEADS_SRC[reason];
  const i = INF_LABELS.indexOf(reason);
  return i >= 0 ? 'icons/inf-' + String(i+1).padStart(2,'0') + '.webp' : null;
}

// Intrinsic pixel size of every illustrated icon, read from the files themselves.
// Feeds the width/height attributes so the browser reserves the right box before
// the image arrives — CSS still decides the rendered size. Re-measure if art changes.
const ICON_DIM = {
  'award-avoided-accident':[453,512],'award-better-late':[476,512],'award-cat-reflexes':[453,512],
  'award-defensive-champion':[512,496],'award-road-ninja':[512,440],'flying-under-radar':[640,540],
  'heads-broken-light':[339,384],'heads-excellent':[371,384],'heads-expired-tags':[281,384],
  'heads-love-style':[384,326],'heads-thank-you':[384,351],
  'inf-01':[256,201],'inf-02':[256,191],'inf-03':[256,188],'inf-04':[256,127],'inf-05':[256,208],
  'inf-06':[256,169],'inf-07':[256,176],'inf-08':[256,194],'inf-09':[256,194],'inf-10':[256,124],
  'inf-11':[256,199],'inf-12':[256,208],'inf-13':[256,193],'inf-14':[256,183],'inf-15':[256,169],
  'inf-16':[256,154],'inf-17':[256,190],'inf-18':[256,125],'inf-19':[256,169],'inf-20':[256,149],
  'inf-21':[256,167],'inf-22':[256,150],'inf-23':[248,256],'inf-24':[256,160],'inf-25':[256,170],
};
function infIconDim(src) {
  const m = /icons\/([a-z0-9-]+)\.webp$/.exec(src || '');
  return (m && ICON_DIM[m[1]]) || null;
}
// Spread onto an <img>: intrinsic size, plus loading="lazy" unless it is above the fold.
function infIconAttrs(src, eager) {
  const d = infIconDim(src);
  if (!d) return eager ? {} : { loading:'lazy' };
  return eager ? { width:d[0], height:d[1] } : { width:d[0], height:d[1], loading:'lazy' };
}

// Refresh button (25 Sep 2026) for Home and The Watch. Runs JA_refreshFeed (store.js) and spins
// while any refresh is running — this one, the app's pull-to-refresh, or coming back to the app.
function RefreshButton({ style }) {
  const [busy, setBusy] = React.useState(!!window.JA_REFRESHING);
  React.useEffect(() => {
    const h = () => setBusy(!!window.JA_REFRESHING);
    window.addEventListener('ja:refresh', h);
    return () => window.removeEventListener('ja:refresh', h);
  }, []);
  return (
    <button type="button" className="btn btn-ghost btn-md ja-refresh" aria-busy={busy} style={style}
      onClick={() => { if (!busy && window.JA_refreshFeed) window.JA_refreshFeed(); }}>
      <Icon name="refresh-cw" size={17} className={busy ? 'ja-spin' : ''} />
      {busy ? 'Refreshing\u2026' : 'Refresh'}
    </button>
  );
}

Object.assign(window, { Icon, Button, RefreshButton, PlateInput, StateSelect, Badge, StatusBadge, Avatar, JerkMeter, Logo, jerkColor, jerkTitle, jaIconSvg, infIconSrc, infIconDim, infIconAttrs });


/* ======== website/SiteCards.jsx ======== */
/* Jerk Alert — shared cards */

// Map a plate's category to a representative illustrated infraction icon
const CAT_REASON = {
  roadrage:'Road Raging', tailgating:'Tailgating', speeding:'Speeding',
  distracted:'Distracted Driving', reckless:'Reckless Driving',
  parking:'Blocked / Took a Parking Spot', rightofway:'Right-of-Way Challenged',
  headsup:'Excellent Driving!',
};

function ListingCard({ b, onOpen, hideCamera }) {
  const positive = b.cat === 'headsup' || b.status === 'cleared';
  const tint = positive ? 'var(--awesome-tint)' : 'var(--ja-alert-tint)';
  const accent = positive ? 'var(--awesome)' : 'var(--ja-alert)';
  const mainReason = CAT_REASON[b.cat] || '';
  const src = infIconSrc(mainReason);
  // distinct alert reasons for this plate, minus the one already shown as the main icon
  const reports = (window.JA_ALERTS && window.JA_ALERTS[b.id]) || [];
  const otherReasons = [...new Set(reports.map(r => r.reason))]
    .filter(r => r !== mainReason && infIconSrc(r));
  return (
    <div className="card listing" onClick={() => onOpen(b)}>
      <div className="thumb" style={{ width:132, height:132, padding:8, background:tint, border:'1.5px solid '+accent }}>
        {src
          ? <img src={src} alt="" {...infIconAttrs(src)} style={{ height:68, width:'auto', maxWidth:'100%', objectFit:'contain', display:'block' }} />
          : <Icon name="shield-check" size={48} color={accent} />}
      </div>
      <div className="col gap-2 grow" style={{ minWidth:0 }}>
        <div className="flex between center gap-3" style={{ flexWrap:'wrap', rowGap:6 }}>
          <div className="flex center gap-2">
            <span className="plate">{b.state} · {b.name}</span>
          </div>
          <StatusBadge status={b.status} />
        </div>
        <JerkMeter level={b.level} size={18} showScore count={b.alerts} />
        <div className="flex center gap-3 card-meta" style={{ marginTop:2 }}>
          <span className="ja-meta card-addr"><Icon name="map-pin" size={13} color="var(--fg3)" /> {b.area.toUpperCase()}</span>
          <span className="ja-meta">{b.catLabel.toUpperCase()}</span>
          <span className="ja-meta">·</span>
          <span className="ja-meta">{b.distance.toUpperCase()}</span>
        </div>
        {otherReasons.length > 0 && (
          <div className="flex center gap-2" style={{ marginTop:4 }}>
            <span className="ja-meta" style={{ marginRight:2 }}>ALSO FLAGGED</span>
            {otherReasons.map(r => (
              <span key={r} title={r} className="flex center"
                style={{ width:40, height:40, flex:'none', borderRadius:'var(--r-md)', padding:6,
                  background:'var(--ja-alert-tint)', border:'1px solid var(--ja-alert)' }}>
                <img src={infIconSrc(r)} alt="" {...infIconAttrs(infIconSrc(r))} style={{ width:'100%', height:'100%', objectFit:'contain', display:'block' }} />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryTile({ c, onOpen }) {
  const src = infIconSrc(CAT_REASON[c.id] || '');
  return (
    <div className="cat-tile" onClick={() => onOpen(c)}>
      <div className="cat-ico" style={{ background: c.color + '1F', width:'100%', maxWidth:200, height:'auto', aspectRatio:'1 / 1', padding:4 }}>
        {src
          ? <img src={src} alt="" {...infIconAttrs(src, true)} style={{ width:'100%', height:'100%', objectFit:'contain', display:'block' }} />
          : <Icon name={c.icon} size={52} color={c.color} sw={2.2} />}
      </div>
      <div className="col gap-1">
        <span style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:16 }}>{c.label}</span>
      </div>
    </div>
  );
}

Object.assign(window, { ListingCard, CategoryTile });


/* ======== website/SiteIncidentMap.jsx ======== */
/* Jerk Alert — Incident map (real Leaflet map + live incident pins) */

function jaPinColor(it) {
  return it.tone === 'thanks' ? 'var(--awesome)'
    : it.tone === 'heads' ? 'var(--ja-orange)'
    : levelColor(it.level);
}

// width/height attrs for the icons built as HTML strings for Leaflet popups and pins
function jaIconDimAttr(src) {
  const d = infIconDim(src);
  return d ? ' width="' + d[0] + '" height="' + d[1] + '"' : '';
}

function jaIncidentPopup(it, color) {
  var src = infIconSrc(it.reason);
  return ''
    + '<div style="font-family:var(--font-body);min-width:172px;line-height:1.3">'
    + '<div style="font-family:var(--font-display);font-weight:900;font-size:13px;color:var(--ja-ink);letter-spacing:.04em">' + it.state + ' &middot; ' + it.plate + '</div>'
    + '<div style="display:flex;align-items:center;gap:7px;margin:4px 0">'
    + (src ? '<img src="' + src + '"' + jaIconDimAttr(src) + ' style="width:26px;height:26px;object-fit:contain;flex:none"/>' : '')
    + '<span style="font-family:var(--font-display);font-weight:900;font-size:15px;color:' + color + '">' + it.reason + '</span></div>'
    + '<div style="font-size:12px;color:#7a8a86">' + it.where + ' &middot; ' + String(it.when).toUpperCase() + '</div>'
    + '<a href="#" data-openplate="' + it.plate + '" data-state="' + it.state + '" style="display:inline-block;margin-top:7px;font-family:var(--font-display);font-weight:800;font-size:13px;color:var(--ja-teal);text-decoration:none">View plate &rarr;</a>'
    + '</div>';
}

// Where the map opens: the incident from the last 7 days nearest the reader (device fix when
// resolved, else the seed centre). No qualifying incident → the reference point itself.
function jaMapStart() {
  const loc = window.JA_LOCATION;
  const ref = (loc && loc.resolved) ? [loc.lat, loc.lng] : (window.JA_MAP_CENTER || [35.0844, -106.6504]);
  const cutoff = Date.now() / 1000 - 7 * 86400;
  let best = null, bd = Infinity;
  (window.JA_INCIDENTS || []).forEach(it => {
    if (it.lat == null || it.lng == null) return;
    if (it.ts && it.ts < cutoff) return;
    const d = Math.hypot(it.lat - ref[0], (it.lng - ref[1]) * Math.cos(ref[0] * Math.PI / 180));
    if (d < bd) { bd = d; best = it; }
  });
  return best ? [best.lat, best.lng] : ref;
}

function IncidentMap({ onOpenBusiness }) {
  const [tick, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    const h = () => force();
    window.addEventListener('ja:update', h);
    return () => window.removeEventListener('ja:update', h);
  }, []);

  const incidents = window.JA_INCIDENTS || [];
  const [active, setActive] = React.useState(null);
  const elRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const userMovedRef = React.useRef(false);   // once the reader drags the map, stop auto-centring

  const openPlate = (plate, state) => {
    const list = window.JA_BUSINESSES || [];
    const b = list.find(x => x.name === plate && (!state || x.state === state)) || list.find(x => x.name === plate);
    if (b && onOpenBusiness) onOpenBusiness(b);
  };

  // init map lazily — only when the panel is near the viewport
  React.useEffect(() => {
    if (mapRef.current || !elRef.current || !window.L) return;
    const el = elRef.current;
    const init = () => {
      if (mapRef.current) return;
      // Zoom 14 reads neighbourhood streets; 12 was the whole metro and the pins read as one blob (owner, 23 Sep 2026).
      const map = L.map(el, { scrollWheelZoom: false }).setView(jaMapStart(), 14);
      map.on('dragstart', () => { userMovedRef.current = true; });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; OpenStreetMap contributors'
      }).addTo(map);
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 80);
      el.addEventListener('click', (e) => {
        const a = e.target.closest('[data-openplate]');
        if (a) { e.preventDefault(); openPlate(a.getAttribute('data-openplate'), a.getAttribute('data-state')); }
      });
      force(); // trigger marker build now that the map exists
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((ents) => {
        if (ents.some(e => e.isIntersecting)) { io.disconnect(); init(); }
      }, { rootMargin: '300px' });
      io.observe(el);
      return () => io.disconnect();
    }
    init();
  }, []);

  // Re-centre on the nearest recent incident when the data or the device fix changes — never
  // on an ordinary re-render (a list tap used to snap the map back), and never after the
  // reader has dragged the map themselves.
  const loc = window.JA_LOCATION;
  const resolved = !!(loc && loc.resolved);
  React.useEffect(() => {
    const map = mapRef.current;
    if (map && !userMovedRef.current) map.setView(jaMapStart());
  }, [resolved, tick]);

  // (re)build markers
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) return;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    incidents.forEach((it, i) => {
      if (it.lat == null || it.lng == null) return;
      const color = jaPinColor(it);
      const src = infIconSrc(it.reason);
      const inner = src
        ? '<img src="' + src + '"' + jaIconDimAttr(src) + ' style="width:30px;height:30px;object-fit:contain"/>'
        : '<span style="color:#fff;font-weight:900;font-size:16px">!</span>';
      const icon = L.divIcon({
        className: '', iconSize: [36, 36], iconAnchor: [18, 18],
        html: '<div style="width:36px;height:36px;border-radius:999px;background:' + color + ';border:2.5px solid #fff;box-shadow:0 3px 8px rgba(30,45,51,.28);display:flex;align-items:center;justify-content:center;overflow:hidden">' + inner + '</div>'
      });
      const m = L.marker([it.lat, it.lng], { icon }).addTo(map);
      m.bindPopup(jaIncidentPopup(it, color));
      m.on('click', () => setActive(i));
      markersRef.current[i] = m;
    });
    // Rebuild only when the data changes (ja:update bumps tick, the map init calls force).
    // Rebuilding on every render tore down the marker whose popup focusIncident just opened.
  }, [tick, incidents.length]);

  const focusIncident = (it, i) => {
    setActive(i);
    const map = mapRef.current, mk = markersRef.current[i];
    if (map && it.lat != null) map.panTo([it.lat, it.lng]);
    if (mk) mk.openPopup();
  };

  // Three tones, same colours as jaPinColor(): alert red, award green, heads-up orange.
  const counts = {
    alerts: incidents.filter(i => i.tone === 'alert').length,
    awards: incidents.filter(i => i.tone === 'thanks').length,
    heads:  incidents.filter(i => i.tone === 'heads').length,
  };
  const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);

  return (
    <section className="section warm" style={{ borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
      <div className="wrap">
        <div className="flex between center wrapw gap-3" style={{ marginBottom: 18 }}>
          <div className="col gap-1">
            <span className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="map-pin" size={14} /> Live near you
            </span>
            <h2 className="ja-h2">The incident map</h2>
          </div>
          <div className="flex center gap-4 wrapw">
            <span className="flex center gap-2 ja-small" style={{ color: 'var(--fg2)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 'var(--r-pill)', background: 'var(--jerk-5)', flex: 'none' }} /> {plural(counts.alerts, 'alert', 'alerts')}
            </span>
            <span className="flex center gap-2 ja-small" style={{ color: 'var(--fg2)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 'var(--r-pill)', background: 'var(--awesome)', flex: 'none' }} /> {plural(counts.awards, 'award', 'awards')}
            </span>
            <span className="flex center gap-2 ja-small" style={{ color: 'var(--fg2)' }}>
              <span style={{ width: 12, height: 12, borderRadius: 'var(--r-pill)', background: 'var(--ja-orange)', flex: 'none' }} /> {plural(counts.heads, 'heads-up', 'heads-ups')}
            </span>
          </div>
        </div>

        <div className="map-layout">
          {/* REAL MAP */}
          <div className="map-panel" style={{ height: 480 }}>
            <div ref={elRef} style={{ position: 'absolute', inset: 0, zIndex: 0 }} />
          </div>

          {/* LIST */}
          <div className="map-list">
            <span className="ja-meta" style={{ padding: '2px 2px 8px' }}>RECENT INCIDENTS</span>
            <div className="map-list-scroll">
              {incidents.length === 0 && (
                <div className="col gap-2" style={{ padding:'16px 10px 8px' }}>
                  <span className="ja-h4" style={{ fontSize:16 }}>No incidents yet</span>
                  <span className="ja-small" style={{ color:'var(--fg2)' }}>Alerts posted near you land on the map as they come in.</span>
                </div>
              )}
              {incidents.map((it, i) => {
                const on = active === i;
                const src = infIconSrc(it.reason);
                return (
                  <div key={i} className={'map-row' + (on ? ' on' : '')} onClick={() => focusIncident(it, i)}>
                    <span className="map-row-ico" style={{ borderColor: jaPinColor(it) }}>
                      {src ? <img src={src} alt="" {...infIconAttrs(src)} /> : <Icon name="triangle-alert" size={16} color={jaPinColor(it)} />}
                    </span>
                    <div className="col grow" style={{ minWidth: 0, gap: 1 }}>
                      <div className="flex center gap-2 wrapw">
                        <span className="plate" style={{ fontSize: 11.5, padding: '1px 6px' }}>{it.state} · {it.plate}</span>
                        {it.tone === 'alert' && <JerkMeter level={it.level} size={12} />}
                      </div>
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13.5, color: 'var(--fg1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.reason}</span>
                      <span className="ja-meta" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.where} · {String(it.when).toUpperCase()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

Object.assign(window, { IncidentMap });


/* ======== website/SiteNav.jsx ======== */
/* Jerk Alert — Top navigation + Footer */

function TopNav({ onHome, onSearch, onPlateLookup, onPost, onWatch, onYou, unread, user, onSignIn }) {
  const [q, setQ] = React.useState('');
  const [st, setSt] = React.useState('NM');
  const go = () => onPlateLookup ? onPlateLookup(q, st) : onSearch(q);
  return (
    <header className="nav">
      <div className="wrap nav-inner">
        <Logo onClick={onHome} />
        <div className="navsearch">
          <div className="seg state-seg">
            <Icon name="map-pin" size={17} color="var(--fg3)" />
            <select value={st} onChange={e=>setSt(e.target.value)} aria-label="State">
              {window.JA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="divider" />
          <div className="seg">
            <Icon name="search" size={17} color="var(--fg3)" />
            <input placeholder="Plate" maxLength={8} value={q} onChange={e=>setQ(e.target.value.toUpperCase())}
              onKeyDown={e=>e.key==='Enter'&&go()} autoCapitalize="characters" autoCorrect="off" spellCheck={false} enterKeyHint="search"
              style={{ textTransform:'uppercase', letterSpacing:'.06em' }} />
          </div>
          <button className="go" onClick={go}><Icon name="arrow-right" size={18} /></button>
        </div>
        <nav className="flex center gap-6" style={{ marginLeft:'auto' }}>
          <a className="navlink nav-states" href="https://jerkalert.org/states" style={{ textDecoration:'none' }}>Plates<span className="nav-states-rest"> by state</span></a>
          <button type="button" className="navlink nav-watch flex center gap-2" onClick={onWatch}><Icon name="bell" size={17} />The Watch</button>
          {/* One account control in the header: the You button (inbox + badge), where the ME
              avatar used to be. Sign out / Settings live on the You page. */}
          {user
            ? <button type="button" className="navlink flex center gap-2" onClick={onYou} title="Your activity"
                style={{ background:'#15677D', color:'#fff', borderRadius:'var(--r-pill)', padding:'8px 16px 8px 13px', minHeight:38 }}>
                <span className="mb-badge-wrap">
                  <Icon name="inbox" size={18} />
                  {unread > 0 && <span className="mb-badge">{unread > 9 ? '9+' : unread}</span>}
                </span>
                You
              </button>
            : <Button variant="ghost" size="md" icon="shield-check" onClick={onSignIn}>Sign In</Button>}
        </nav>
      </div>
    </header>
  );
}

function Footer({ onWatch, onPost, onSignIn, onSection }) {
  // Every link goes somewhere real: `act` stays inside the app, `href` leaves it.
  const section = (id) => () => onSection && onSection(id);
  const cols = [
    { h:'Jerk Alert', links:[
      { l:'How it works', href:'https://jerkalert.org/how-it-works' },
      { l:'Become a Lookout', act:onSignIn },
      { l:'Post an Alert', act:onPost },
    ] },
    { h:'Explore', links:[
      { l:'Recent alerts', act:onWatch },
      { l:'Trending jerks', act:section('trending') },
      { l:'Browse categories', act:section('categories') },
      { l:'Plates by state', href:'https://jerkalert.org/states' },
    ] },
    { h:'Company', links:[
      { l:'About', href:'https://jerkalert.org/about' },
      { l:'Careers', href:'mailto:support@jerkalert.org' },
      { l:'Press', href:'https://jerkalert.org/press' },
      { l:'Contact', href:'mailto:support@jerkalert.org' },
    ] },
  ];
  return (
    <footer className="footer">
      <div className="wrap">
        <div className="flex between wrapw gap-8" style={{ alignItems:'flex-start' }}>
          <div className="col gap-4" style={{ maxWidth:300 }}>
            <div className="logo">
              <img src="assets/logo-mark.webp" style={{ height:40 }} alt="" />
              <span className="logo-word" style={{ fontSize:22 }}>
                <span style={{ color:'#fff' }}>jerk</span><span style={{ color:'var(--ja-alert)' }}>alert</span>
              </span>
            </div>
            <p style={{ color:'rgba(255,248,240,.6)', fontSize:14, lineHeight:1.6, margin:0 }}>
              Real warnings from real lookouts. Alert the jerk, stay safe, move on.
            </p>
          </div>
          <div className="flex wrapw gap-8 grow" style={{ justifyContent:'flex-end' }}>
            {cols.map(c => (
              <div key={c.h} className="col" style={{ minWidth:140 }}>
                <h5>{c.h}</h5>
                {c.links.map(x => x.href
                  ? <a key={x.l} href={x.href} target={x.href.indexOf('mailto:') === 0 ? undefined : '_blank'} rel="noreferrer">{x.l}</a>
                  : <a key={x.l} href="#" onClick={e => { e.preventDefault(); if (x.act) x.act(); }}>{x.l}</a>)}
              </div>
            ))}
          </div>
        </div>
        <div style={{ borderTop:'1px solid var(--line-ink)', marginTop:32, paddingTop:20 }}
          className="flex between wrapw gap-3">
          <span style={{ color:'rgba(255,248,240,.5)', fontSize:13 }}>© 2026 Jerk Alert LLC · A community watch project</span>
          <span className="flex center gap-3" style={{ fontSize:13 }}>
            <a href="legal/privacy.html" style={{ color:'rgba(255,248,240,.72)', display:'inline-flex', alignItems:'center', minHeight:44 }}>Privacy</a>
            <a href="legal/terms.html" style={{ color:'rgba(255,248,240,.72)', display:'inline-flex', alignItems:'center', minHeight:44 }}>Terms</a>
            <a href="safety.html" style={{ color:'rgba(255,248,240,.72)', display:'inline-flex', alignItems:'center', minHeight:44 }}>Safety</a>
            <a href="mailto:support@jerkalert.org" style={{ color:'rgba(255,248,240,.72)', display:'inline-flex', alignItems:'center', minHeight:44 }}>Support</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

Object.assign(window, { TopNav, Footer });


/* ======== website/SiteHome.jsx ======== */
/* Jerk Alert — Home page */

function Home({ onSearch, onPlateLookup, onOpenBusiness, onOpenCategory, onPost }) {
  const [q, setQ] = React.useState('');
  const [st, setSt] = React.useState('NM');
  const go = () => onPlateLookup ? onPlateLookup(q, st) : onSearch(q);
  const trending = window.JA_BUSINESSES.filter(b => ['offender','trending'].includes(b.status)).slice(0,4);
  const watch = window.JA_BUSINESSES;
  const browse = [
    { label:'Frequent flyers', sort:'most' },
    { label:'Random', sort:'random' },
    { label:'New', sort:'recent' },
    { label:'Top', sort:'jerkiest' },
  ];

  return (
    <main>
      {/* HERO */}
      <section className="hero">
        <div className="wrap hero-grid" style={{ padding:'60px 24px 72px', position:'relative', zIndex:1, maxWidth:1140 }}>
          <div className="col" style={{ minWidth:0 }}>
          <span className="eyebrow">Alert the jerk. Stay safe. Move on.</span>
          <h1 className="ja-h1" style={{ fontSize:'clamp(38px, 9vw, 60px)', margin:'14px 0 16px', maxWidth:620 }}>
            Somewhere,<br/>someone else is honking at <span style={{ color:'var(--ja-alert)' }}>them</span> too!
          </h1>
          <p className="ja-lead" style={{ maxWidth:560, marginBottom:30 }}>
            Run a plate. See the record.
          </p>
          <div className="hero-search" style={{ maxWidth:560 }}>
            <div className="seg state-seg">
              <Icon name="map-pin" size={20} color="var(--fg3)" />
              <select value={st} onChange={e=>setSt(e.target.value)} aria-label="State">
                {window.JA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="divider" />
            <div className="seg">
              <Icon name="search" size={20} color="var(--fg3)" />
              <input placeholder="Plate #" maxLength={8} value={q}
                onChange={e=>setQ(e.target.value.toUpperCase())} onKeyDown={e=>e.key==='Enter'&&go()}
                autoCapitalize="characters" autoCorrect="off" spellCheck={false} enterKeyHint="search"
                style={{ textTransform:'uppercase', letterSpacing:'.08em', fontWeight:700 }} />
            </div>
            <button className="go" onClick={go}><Icon name="search" size={18} />Search</button>
          </div>
          <div className="flex center gap-2 wrapw" style={{ marginTop:18 }}>
            <span className="ja-meta" style={{ marginRight:4 }}>POPULAR:</span>
            {browse.map(x => (
              <span key={x.label} onClick={()=>onSearch('', x.sort)} className="pop-chip"
                style={{ cursor:'pointer', fontFamily:'var(--font-display)', fontWeight:800, fontSize:13, color:'var(--ja-teal)', background:'#fff', border:'1.5px solid var(--line-strong)', borderRadius:'var(--r-pill)', padding:'6px 13px' }}>
                {x.label}
              </span>
            ))}
          </div>
          </div>
          <div className="hero-mascot flex center">
            <img src="assets/mascot.webp" alt="Jerk Alert mascot — a safety officer blowing the whistle" />
          </div>
        </div>
      </section>

      {/* INCIDENT MAP */}
      <IncidentMap onOpenBusiness={onOpenBusiness} />

      {/* TRENDING STRIP */}
      <section className="section warm" id="trending" style={{ borderTop:'1px solid var(--line)', borderBottom:'1px solid var(--line)', scrollMarginTop:80 }}>
        <div className="wrap">
          <div className="flex between center" style={{ marginBottom:8 }}>
            <div className="col gap-1">
              <span className="eyebrow" style={{ display:'flex', alignItems:'center', gap:6 }}>
                <Icon name="trending-up" size={14} /> Trending this week
              </span>
              <h2 className="ja-h2">Most flagged plates in your area</h2>
            </div>
            <Button variant="ghost" size="md" icon="bell" onClick={()=>onSearch('')}>Watch Your Area</Button>
          </div>
          <div className="col gap-4" style={{ marginTop:20 }}>
            {trending.length === 0 && (
              <div className="card col center gap-3 pad-card" style={{ padding:48, textAlign:'center' }}>
                <img src="assets/logo-mark.webp" width="512" height="512" style={{ width:84, height:'auto', opacity:.9 }} alt="" />
                <h3 className="ja-h3" style={{ margin:0 }}>Nothing trending yet</h3>
                <p className="ja-body" style={{ color:'var(--fg2)', margin:0 }}>No plates have been flagged this week. Post the first alert and it shows up here.</p>
                <Button variant="primary" size="md" icon="siren" onClick={onPost}>Post an Alert</Button>
              </div>
            )}
            {trending.map(b => <ListingCard key={b.id} b={b} onOpen={onOpenBusiness} hideCamera />)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="section" id="categories" style={{ scrollMarginTop:80 }}>
        <div className="wrap">
          <div className="flex between center" style={{ marginBottom:24 }}>
            <h2 className="ja-h2">Browse by category</h2>
          </div>
          <div className="cat-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16 }}>
            {window.JA_CATEGORIES.map(c => <CategoryTile key={c.id} c={c} onOpen={onOpenCategory} />)}
          </div>
        </div>
      </section>

      {/* HOW + CTA band */}
      <section className="section">
        <div className="wrap how-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:24 }}>
          {[
            { icon:'search', title:'Alert the Jerk', text:'Run a plate or browse by behavior. See what the crew has already flagged.' },
            { icon:'shield-alert', title:'Stay Safe', text:'Check the Erk Meter and recent alerts before you share the road with them.' },
            { icon:'shield-check', title:'Move On', text:'Post the Alert, let the record do the talking, and get on with your day. No confrontation, no chasing anyone down.' },
          ].map((s,i) => (
            <div key={i} className="col gap-3">
              <div className="cat-ico" style={{ background:'var(--ja-alert-tint)', width:52, height:52 }}>
                <Icon name={s.icon} size={26} color="var(--ja-alert)" sw={2.2} />
              </div>
              <h3 className="ja-h3" style={{ margin:0 }}>{s.title}</h3>
              <p className="ja-body" style={{ margin:0, color:'var(--fg2)' }}>{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT / FOUNDER STORY */}
      <section className="section warm" id="about" style={{ borderTop:'1px solid var(--line)' }}>
        <div className="wrap about-grid" style={{ display:'grid', gridTemplateColumns:'minmax(0,360px) 1fr', gap:48, alignItems:'center' }}>
          <div className="col center gap-4" style={{ background:'#fff', border:'1px solid var(--line)', borderRadius:'var(--r-xl)', boxShadow:'var(--sh-sm)', padding:'40px 28px' }}>
            <img src="assets/logo-mark.webp" style={{ width:160 }} alt="Jerk Alert mascot" />
            <span className="ja-meta" style={{ textAlign:'center' }}>ALERT THE JERK · STAY SAFE · MOVE ON</span>
          </div>
          <div className="col gap-4">
            <span className="eyebrow">Why we built this</span>
            <h2 className="ja-h2" style={{ maxWidth:560 }}>A friendly heads-up, before it's a hard lesson.</h2>
            <p className="ja-lead" style={{ color:'var(--fg2)', maxWidth:600, margin:0 }}>
              I'll be honest — this one's personal. As a teenager, I drove like a bit of a jerk. I've always
              wished someone could have quietly, anonymously let my parents know what I was doing out on the road.
              Now I've got young kids of my own, and that worry hits a whole lot closer to home.
            </p>
            <p className="ja-body" style={{ color:'var(--fg2)', maxWidth:600, margin:0 }}>
              Driving has become more out of control than ever. But here's the thing that's also changed: dashcams
              are more ubiquitous than ever. A camera could be rolling at any moment — and we all need to realize
              that. The world is a lot smaller than it used to be, and what we do out there doesn't just disappear.
            </p>
            <p className="ja-body" style={{ color:'var(--fg2)', maxWidth:600, margin:0 }}>
              Jerk Alert is the heads-up we wished existed — a way for the crew to alert the jerk, stay safe,
              and maybe nudge someone toward being a little more excellent. We warn. We don't pile on.
            </p>
            <span style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:15, color:'var(--ja-teal)', marginTop:4 }}>— The Jerk Alert crew</span>
          </div>
        </div>
      </section>

      {/* DARK CTA */}
      <section style={{ background:'var(--ja-ink)', color:'#fff' }}>
        <div className="wrap section flex between center wrapw gap-6">
          <div className="col gap-2" style={{ maxWidth:560 }}>
            <h2 className="ja-h2" style={{ color:'#fff' }}>Saw something? Say something.</h2>
            <p className="ja-lead" style={{ color:'rgba(255,248,240,.7)' }}>
              Your alert could keep the next driver out of harm’s way. It takes two minutes.
            </p>
          </div>
          <Button variant="primary" size="lg" icon="siren" onClick={onPost}>Post an Alert</Button>
        </div>
      </section>
    </main>
  );
}

Object.assign(window, { Home });


/* ======== website/SiteResults.jsx ======== */
/* Jerk Alert — Search results page */

function FilterGroup({ title, children }) {
  return (
    <div className="col gap-3" style={{ paddingBottom:20, borderBottom:'1px solid var(--line)', marginBottom:20 }}>
      <h4 className="ja-h4" style={{ fontSize:14, margin:0 }}>{title}</h4>
      {children}
    </div>
  );
}

function Check({ label, on, onClick, count }) {
  return (
    <div className="flex center between filter-check" onClick={onClick} style={{ cursor:'pointer' }}>
      <span className="flex center gap-2">
        <span style={{ width:18, height:18, borderRadius:6, border:'1.5px solid '+(on?'var(--ja-alert)':'var(--line-strong)'),
          background:on?'var(--ja-alert)':'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
          {on && <Icon name="check" size={13} color="#fff" sw={3} />}
        </span>
        <span className="ja-small" style={{ color:'var(--fg1)' }}>{label}</span>
      </span>
      {count != null && <span className="ja-meta">{count}</span>}
    </div>
  );
}

function Results({ query, initialSort, onOpenBusiness, onPost }) {
  const [cats, setCats] = React.useState({});
  const [minLevel, setMinLevel] = React.useState(0);
  const [filtersOpen, setFiltersOpen] = React.useState(false);   // phones only; the aside is always visible above 900px
  const [sort, setSort] = React.useState(initialSort || 'relevance');
  React.useEffect(() => { setSort(initialSort || 'relevance'); }, [initialSort]);
  const activeFilters = Object.keys(cats).filter(k => cats[k]).length + (minLevel ? 1 : 0);

  const list = React.useMemo(() => {
    let l = window.JA_BUSINESSES.slice();
    const active = Object.keys(cats).filter(k => cats[k]);
    if (active.length) l = l.filter(b => active.includes(b.cat));
    if (minLevel) l = l.filter(b => b.level >= minLevel);
    if (sort === 'jerkiest') l.sort((a,b)=>b.level-a.level);
    else if (sort === 'most') l.sort((a,b)=>b.alerts-a.alerts);
    else if (sort === 'recent') l.reverse();
    else if (sort === 'random') l.sort(()=>Math.random()-0.5);
    return l;
  }, [cats, minLevel, sort]);

  const toggleCat = id => setCats(c => ({ ...c, [id]: !c[id] }));

  return (
    <main style={{ background:'var(--bg-warm)', flex:1 }}>
      {/* summary bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid var(--line)' }}>
        <div className="wrap flex between center wrapw gap-3 results-head" style={{ padding:'18px 24px' }}>
          <div className="col gap-2" style={{ minWidth:0 }}>
            <h1 className="ja-h3" style={{ margin:0, lineHeight:1.2 }}>
              {query ? `Alerts for \u201C${query}\u201D` : 'Recent alerts'}{window.JA_LOCATION && window.JA_LOCATION.label && <span className="loc-label" style={{ color:'var(--fg3)', fontWeight:700 }}> · {window.JA_LOCATION.label}</span>}
            </h1>
            <span className="ja-meta">{list.length} RESULT{list.length === 1 ? '' : 'S'}</span>
          </div>
          <div className="flex center gap-2 sort-ctl">
            <span className="ja-meta">SORT</span>
            <select className="field" style={{ width:'auto', padding:'8px 12px', fontWeight:600 }}
              value={sort} onChange={e=>setSort(e.target.value)}>
              <option value="relevance">Popular</option>
              <option value="most">Frequent flyers</option>
              <option value="random">Random</option>
              <option value="recent">New</option>
              <option value="jerkiest">Top</option>
            </select>
          </div>
        </div>
      </div>

      <div className="wrap" style={{ padding:'28px 24px' }}>
        <button className="filter-toggle" onClick={()=>setFiltersOpen(o=>!o)} aria-expanded={filtersOpen}>
          <Icon name="sliders-horizontal" size={16} />
          Filters{activeFilters ? ' · ' + activeFilters : ''}
          <Icon name={filtersOpen ? 'chevron-up' : 'chevron-down'} size={16} />
        </button>
        <div className="results-grid" style={{ display:'grid', gridTemplateColumns:'240px 1fr 340px', gap:28, alignItems:'start' }}>
          {/* FILTERS */}
          <aside className={'card filter-aside' + (filtersOpen ? ' open' : '')} style={{ padding:20, position:'sticky', top:84 }}>
            <h3 className="ja-h4" style={{ margin:'0 0 18px' }}>Filters</h3>
            <FilterGroup title="Category">
              <div className="col gap-3">
                {window.JA_CATEGORIES.slice(0,5).map(c =>
                  <Check key={c.id} label={c.label} on={!!cats[c.id]} onClick={()=>toggleCat(c.id)} />)}
              </div>
            </FilterGroup>
            <FilterGroup title="Minimum Erk Score">
              <div className="flex center gap-2">
                {[0,2,3,4].map(l => (
                  <span key={l} onClick={()=>setMinLevel(l)} className="pill"
                    style={{ padding:'6px 12px', fontSize:13, ...(minLevel===l?{background:'var(--ja-ink)',color:'#fff',borderColor:'var(--ja-ink)'}:{}) }}>
                    {l===0?'Any':l+'+'}
                  </span>
                ))}
              </div>
            </FilterGroup>
            <Button variant="ghost" size="md" icon="rotate-ccw" onClick={()=>{setCats({});setMinLevel(0);}} style={{ width:'100%' }}>Reset</Button>
          </aside>

          {/* LIST */}
          <div className="col gap-4">
            {list.length === 0 && (
              <div className="card col center gap-3 pad-card" style={{ padding:48, textAlign:'center' }}>
                <img src="assets/logo-mark.webp" width="512" height="512" style={{ width:84, height:'auto', opacity:.9 }} alt="" />
                <h3 className="ja-h3" style={{ margin:0 }}>All clear here</h3>
                <p className="ja-body" style={{ color:'var(--fg2)', margin:0 }}>No plates match these filters. Be the first lookout.</p>
                <Button variant="primary" size="md" icon="siren" onClick={onPost}>Post an Alert</Button>
              </div>
            )}
            {list.map(b => <ListingCard key={b.id} b={b} onOpen={onOpenBusiness} />)}
          </div>

          {/* MAP */}
          <div className="map-col" style={{ position:'sticky', top:84 }}>
            <MapPanel list={list} onOpenBusiness={onOpenBusiness} />
          </div>
        </div>
      </div>
    </main>
  );
}

function jaHash(s) { let h = 0; s = String(s); for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0; } return Math.abs(h); }

function MapPanel({ list, onOpenBusiness }) {
  const elRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef([]);

  React.useEffect(() => {
    if (mapRef.current || !elRef.current || !window.L) return;
    const map = L.map(elRef.current, { scrollWheelZoom: false }).setView([39.5, -98.35], 3.4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 80);
  }, []);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) return;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    const pts = [];
    list.forEach(b => {
      const c = (window.JA_STATE_LATLNG || {})[b.state];
      if (!c) return;
      const seed = jaHash(b.id);
      const ll = [c[0] + ((seed % 100) - 50) / 900, c[1] + (((seed >> 4) % 100) - 50) / 900];
      pts.push(ll);
      const color = jerkColor(b.level);
      const icon = L.divIcon({
        className: '', iconSize: [32, 40], iconAnchor: [16, 40],
        html: '<div style="display:flex;flex-direction:column;align-items:center"><span style="background:' + color + ';color:#fff;font-family:var(--font-display);font-weight:900;font-size:13px;width:32px;height:32px;border-radius:999px;display:flex;align-items:center;justify-content:center;border:2.5px solid #fff;box-shadow:0 3px 8px rgba(30,45,51,.28)">' + b.level.toFixed(1) + '</span><span style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:7px solid #fff;margin-top:-1px"></span></div>'
      });
      const m = L.marker(ll, { icon }).addTo(map);
      m.bindTooltip(b.state + ' \u00b7 ' + b.name);
      m.on('click', () => onOpenBusiness(b));
      markersRef.current.push(m);
    });
    if (pts.length) { try { map.fitBounds(pts, { padding: [40, 40], maxZoom: 9 }); } catch (e) {} }
  }, [list]);

  return (
    <div className="mappanel" style={{ height: 480 }}>
      <div ref={elRef} style={{ position: 'absolute', inset: 0, zIndex: 0 }} />
      <div style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 500, background: '#fff', borderRadius: 'var(--r-pill)', padding: '8px 14px', boxShadow: 'var(--sh-sm)', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13 }}>
        <span className="flex center gap-2"><Icon name="map-pin" size={15} color="var(--ja-alert)" />{list.length} plates</span>
      </div>
    </div>
  );
}

Object.assign(window, { Results });


/* ======== website/SitePlacePicker.jsx ======== */
/* Jerk Alert — location picker: city first, then the spot inside it.

   Why two fields. A single free-text box left us guessing which city "Burnside and 12th" meant,
   so every junction lookup searched a huge box and timed out. Naming the city first anchors
   everything: the junction query gets a 12-mile box, street names are looked up inside that city,
   and nothing is ever typed freely — city, street, corner and highway all come from a list.
   The city itself is always the last suggestion, so a lookout can still post when OSM has never
   heard of the corner they mean.

   On a freeway the spot is a stretch, not a point: typing "I-25" offers northbound/southbound
   through the chosen city. Nobody remembers exit numbers, and a repeat offender works the same
   stretch anyway. Exit numbers still resolve for the rare lookout who knows one. */

// 11 Sep 2026: the picker no longer remembers the last city (ja_last_city) and never opens with
// anything pre-filled — every Alert starts with two empty fields. Typed text that has not been
// matched to a suggestion is NOT a location: the caller is told "unresolved" and cannot post
// until the lookout picks from the list or clears the field.

function SuggestList({ loading, items, empty, onPick }) {
  return (
    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30,
      background: '#fff', border: '1.5px solid var(--line-strong)', borderRadius: 'var(--r-md)',
      boxShadow: 'var(--sh-lg)', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
      {loading && <div className="ja-small" style={{ padding: '11px 14px', color: 'var(--fg3)' }}>Searching…</div>}
      {!loading && items.length === 0 && <div className="ja-small" style={{ padding: '11px 14px', color: 'var(--fg3)' }}>{empty}</div>}
      {!loading && items.map((it, i) => (
        <button key={i} type="button" className="place-opt" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(it)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
            padding: '11px 14px', border: 'none', borderTop: i ? '1px solid var(--line)' : 'none',
            background: 'none', cursor: 'pointer', font: 'inherit', minHeight: 44 }}>
          <Icon name={it.kind === 'corner' ? 'route' : it.kind === 'city' ? 'building-2' : it.kind === 'highway' ? 'milestone' : 'map-pin'}
            size={16} color="var(--ja-teal)" style={{ flex: 'none' }} />
          <span className="ja-small" style={{ fontWeight: 600, color: 'var(--fg1)' }}>{it.label}</span>
        </button>
      ))}
    </div>
  );
}

function PlacePicker({ stateCode, place, onPick, onUnresolved, placeholder }) {
  const [city, setCity] = React.useState(null);
  const [cq, setCq] = React.useState('');
  const [cityItems, setCityItems] = React.useState([]);
  const [cityOpen, setCityOpen] = React.useState(false);
  const [cityBusy, setCityBusy] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const cityTimer = React.useRef(null);
  const spotTimer = React.useRef(null);
  const cityBox = React.useRef(null);
  const spotBox = React.useRef(null);

  // Choosing a city is already a valid location: a report needs a city, not a corner. So the city
  // is published to the caller the moment it is picked, and a corner (if the lookout bothers with
  // one) just replaces it with something more precise. The Where field never has to be touched.
  const lastPlace = React.useRef(place);
  React.useEffect(() => {
    if (place === lastPlace.current) return;
    lastPlace.current = place;
    if (place && place.kind !== 'city' && place.label !== q) setQ(place.label);
  }, [place]);

  const runCity = (val) => {
    clearTimeout(cityTimer.current);
    if (val.trim().length < 2) { setCityItems([]); setCityOpen(false); setCityBusy(false); return; }
    setCityBusy(true); setCityOpen(true);
    cityTimer.current = setTimeout(() => {
      window.JA_searchCities(val, stateCode).then(list => { setCityItems(list || []); setCityBusy(false); });
    }, 450);
  };
  const runSpot = (val, c) => {
    clearTimeout(spotTimer.current);
    const useCity = c || city;
    if (!useCity || val.trim().length < 2) { setItems([]); setOpen(false); setBusy(false); return; }
    setBusy(true); setOpen(true);
    spotTimer.current = setTimeout(() => {
      window.JA_searchPlaces(val, stateCode, useCity).then(list => { setItems(list || []); setBusy(false); });
    }, 450);
  };

  const chooseCity = (c) => {
    setCity(c); setCq(c.label); setCityItems([]); setCityOpen(false);
    onPick(c);                                   // city-level location, good enough to post
    if (onUnresolved) onUnresolved(false);
    setQ('');                                    // a new city means the old spot text is meaningless
  };
  const chooseSpot = (it) => { onPick(it); setQ(it.label); setItems([]); setOpen(false); if (onUnresolved) onUnresolved(false); };

  return (
    <div className="col gap-2">
      <div ref={cityBox} style={{ position: 'relative' }}
        onBlur={(e) => { if (!cityBox.current.contains(e.relatedTarget)) setCityOpen(false); }}>
        <span style={{ position: 'absolute', left: 14, top: 21, transform: 'translateY(-50%)', pointerEvents: 'none',
          color: city ? 'var(--awesome)' : 'var(--ja-teal)', display: 'flex', zIndex: 1 }}>
          <Icon name={city ? 'check-circle' : 'building-2'} size={18} />
        </span>
        <input className="field" value={city ? city.label : cq} placeholder="City where it happened — e.g. Albuquerque"
          style={{ paddingLeft: 42 }} autoComplete="off" autoCorrect="off"
          onChange={(e) => { const v = e.target.value; setCity(null); onPick(null); setCq(v); setQ(''); if (onUnresolved) onUnresolved(false); runCity(v); }}
          onFocus={() => { if (cityItems.length) setCityOpen(true); }} />
        {cityOpen && (
          <SuggestList loading={cityBusy} items={cityItems} onPick={chooseCity}
            empty="No city by that name — keep typing." />
        )}
      </div>
      <div ref={spotBox} style={{ position: 'relative' }}
        onBlur={(e) => { if (!spotBox.current.contains(e.relatedTarget)) setOpen(false); }}>
        <span style={{ position: 'absolute', left: 14, top: 21, transform: 'translateY(-50%)', pointerEvents: 'none',
          color: place && place.kind !== 'city' ? 'var(--awesome)' : 'var(--ja-teal)', display: 'flex', zIndex: 1 }}>
          <Icon name={place && place.kind !== 'city' ? 'check-circle' : 'search'} size={18} />
        </span>
        <input className="field" value={q} disabled={!city}
          placeholder={city ? (placeholder || 'Cross street or highway — optional') : 'Pick a city first'}
          style={{ paddingLeft: 42, opacity: city ? 1 : .6 }} autoComplete="off" autoCorrect="off"
          onChange={(e) => {
            const v = e.target.value; setQ(v); runSpot(v);
            // Free text is not a place. Empty → back to the city; anything else → unresolved, no post.
            if (v.trim() === '') { if (city) onPick(city); if (onUnresolved) onUnresolved(false); }
            else if (onUnresolved) onUnresolved(true);
          }}
          onFocus={() => { if (items.length) setOpen(true); }} />
        {open && (
          <SuggestList loading={busy} items={items} onPick={chooseSpot}
            empty={<span>Nothing matched in {city ? city.label : 'that city'} — try a corner (“Burnside &amp; 12th”), a highway (“I-25”), or just one street.</span>} />
        )}
      </div>
      <span className="ja-meta" style={{ color: city && q.trim() && !(place && place.kind !== 'city') ? 'var(--ja-alert)' : 'var(--fg3)' }}>
        {city && q.trim() && !(place && place.kind !== 'city')
          ? 'Pick a suggestion from the list, or clear this field to post with just the city.'
          : city && place && place.kind !== 'city'
            ? 'Exact spot set — clear the field to go back to the city.'
            : 'Optional — the city on its own is enough to post.'}
      </span>
    </div>
  );
}

Object.assign(window, { PlacePicker });


/* ======== website/SitePlateDisplay.jsx ======== */
/* Jerk Alert — SitePlateDisplay.jsx  (v3 — sprite engine)
   Exposes: window.PlateGallerySlot, PlateEntry, plateFields (personalized plates, 27 Sep 2026)

   v2 drew the number with Google web fonts and carried its own 51-row layout
   table. Both are gone: lettering now comes from the shared sprite engine in
   plate-sprite.js, which is the same code the tuning tool uses, so the site and
   the tool can no longer drift apart. Load plate-sprite.js BEFORE this file.

   The frame, bolts and dealer strip are unchanged from v2.
*/

// The plate is composed straight into this component's own <canvas>. An earlier
// draft cached each plate as a base64 data URL instead — ~1.6 MB per plate, held
// for the life of the page. On a 512 MB phone browsing plate details that adds up
// fast, and a canvas is freed with its component for nothing.
const PD_MAX = 1440;   // ceiling: 2x the 720px the frame renders at on desktop

const BOLT = {
  position:'absolute', width:11, height:11, borderRadius:'50%',
  background:'radial-gradient(circle at 35% 35%, #d0d0d0, #888 60%, #666)',
  boxShadow:'0 1px 3px rgba(0,0,0,.5), inset 0 1px 2px rgba(255,255,255,.3)',
  zIndex:10,
};

function PlateGallerySlot({ stateCode, number, look }) {
  const wrapRef = React.useRef(null);
  const cvRef = React.useRef(null);
  const [res, setRes] = React.useState(0);
  const [drawn, setDrawn] = React.useState(false);
  const [noArt, setNoArt] = React.useState(false);
  const [artTry, setArtTry] = React.useState(0);
  const num = (number || '').toUpperCase().replace(/[^A-Z0-9 -]/g, '');
  // A personalized look (reports.plate_look, via store.js) is drawn exactly as saved — no state mark.
  const lk = look ? String(look).toUpperCase().replace(/[^A-Z0-9 -]/g, '') : '';

  // Compose at the size actually shown, not a fixed maximum. A phone card is
  // ~340 CSS px, so a flat 1120 would have cost ~3x the pixels it can display —
  // and the backing store is the real memory here, not the markup.
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const measure = (cw) => {
      if (!(cw > 0)) return;
      const want = Math.min(PD_MAX, Math.round(cw * dpr));
      // Ignore sub-10% resizes; recomposing on every drag frame is wasteful.
      setRes(prev => (prev && Math.abs(want - prev) / prev < 0.1 ? prev : want));
    };
    const ro = new ResizeObserver(e => measure(e[0].contentRect.width));
    ro.observe(el);
    measure(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    let live = true;
    setDrawn(false);
    if (window.JAPlate && cvRef.current && res) {
      window.JAPlate
        .compose({ state: stateCode, number: lk || num, personal: !!lk, width: res, canvas: cvRef.current })
        .then(() => { if (live) setDrawn(true); })
        .catch(() => {});
    }
    return () => { live = false; };
  }, [stateCode, num, lk, res]);

  // The bare art shows underneath while the sheet loads, so the card never
  // flashes empty. Filenames come in two spellings (lowercase key, Title Case
  // name) and several extensions, so walk the engine's candidate list on error
  // and settle on a blank plate only once it is exhausted.
  const artURL = window.JAPlate && !noArt ? window.JAPlate.artURL(stateCode, artTry) : null;

  return (
    <div style={{
      width:'100%', height:'100%',
      background:'linear-gradient(180deg,#05070a 0%,#07090d 52%,#14171b 86%,#1c2126 100%)',
      display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', position:'relative',
    }}>
      {/* Bumper. A normal-flow wrapper padded around the plate pocket, so its height comes
         from the plate and not from the row height — the gallery row is `height:auto` on
         phones, which left percentage-positioned layers with nothing to sit outside of.
         Gradients only: it scales with the slot and costs nothing to load. */}
      <div style={{
        position:'relative', width:'112%', flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        padding:'8% 5% 6.5%', borderRadius:'30px 30px 18px 18px',
        background:'linear-gradient(180deg,#8d9aa7 0%,#63707d 7%,#4a5561 22%,#3c4650 50%,#2b333b 78%,#1d232a 100%)',
        boxShadow:'inset 0 2px 0 rgba(255,255,255,.4),inset 0 -26px 40px rgba(0,0,0,.45),0 26px 32px -12px rgba(0,0,0,.9)',
      }}>
        {/* specular sweep along the top roll */}
        <div aria-hidden="true" style={{
          position:'absolute', left:0, right:0, top:0, height:'20%', borderRadius:'30px 30px 0 0',
          background:'linear-gradient(180deg,rgba(255,255,255,.30),rgba(255,255,255,.05) 60%,rgba(255,255,255,0))',
        }} />
        {/* body crease */}
        <div aria-hidden="true" style={{
          position:'absolute', left:0, right:0, top:'21%', height:2,
          background:'linear-gradient(180deg,rgba(0,0,0,.5),rgba(255,255,255,.16))',
        }} />
        {/* the bumper rolls away at both ends */}
        <div aria-hidden="true" style={{
          position:'absolute', inset:0, borderRadius:'30px 30px 18px 18px',
          background:'linear-gradient(90deg,rgba(0,0,0,.62),rgba(0,0,0,0) 18%,rgba(0,0,0,0) 82%,rgba(0,0,0,.62))',
        }} />
        {/* recessed pocket the plate bolts into */}
        <div style={{
          position:'relative', zIndex:2, width:'100%', maxWidth:640, flexShrink:0,
          padding:'13px 15px 16px', borderRadius:9,
          background:'linear-gradient(180deg,#10151a 0%,#1b222a 55%,#242c35 100%)',
          boxShadow:'inset 0 5px 12px rgba(0,0,0,.85),inset 0 -1px 0 rgba(255,255,255,.08),0 1px 0 rgba(255,255,255,.07)',
        }}>
      {/* aluminium dealer frame */}
        <div style={{
        width:'100%', maxWidth:610,
        background:'linear-gradient(160deg,#c8c8c8 0%,#909090 40%,#a8a8a8 60%,#c0c0c0 100%)',
        padding:'6px 6px 15px', borderRadius:6, position:'relative', flexShrink:0,
        boxShadow:'inset 0 2px 3px rgba(255,255,255,.4),inset 0 -3px 5px rgba(0,0,0,.45),0 10px 26px rgba(0,0,0,.75)',
      }}>
        <div style={{
          position:'absolute', bottom:2, left:'50%', transform:'translateX(-50%)',
          fontFamily:"'Share Tech Mono',monospace,'Courier New'",
          fontSize:8, color:'#555', letterSpacing:'1.5px', whiteSpace:'nowrap', userSelect:'none',
        }}>JERKALERT.ORG</div>

        <div ref={wrapRef} style={{
          position:'relative', width:'100%', aspectRatio:'2 / 1',
          overflow:'hidden', borderRadius:3,
          background:'#fff', boxShadow:'inset 0 0 0 1px rgba(0,0,0,.15)',
        }}>
          {artURL && <img
            src={artURL} alt=""
            onError={() => {
              const next = artTry + 1;
              if (window.JAPlate && window.JAPlate.artURL(stateCode, next)) setArtTry(next);
              else setNoArt(true);
            }}
            style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'fill', borderRadius:3 }}
          />}
          <canvas
            ref={cvRef} role="img" aria-label={`${stateCode || ''} plate ${lk || num}`}
            style={{ position:'absolute', inset:0, width:'100%', height:'100%', borderRadius:3,
                     opacity: drawn ? 1 : 0, transition:'opacity .12s linear' }}
          />
          <div style={{ ...BOLT, top:6, left:14 }} />
          <div style={{ ...BOLT, top:6, right:14 }} />
          <div style={{ ...BOLT, bottom:6, left:14 }} />
          <div style={{ ...BOLT, bottom:6, right:14 }} />
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}

/* ── Personalized plates (27 Sep 2026, owner) ─────────────────────────────────────────────────
   A personalized plate is struck without its state's standard mark (NC JERKY, not JER-KY) and may
   carry its own spaces or a dash. PlateEntry is the plate block of the Post Alert, Post an Award and
   Friendly Heads Up sheets: PlateInput + StateSelect plus a Personalized button. With it on, the
   field shows exactly what the plate shows, spaces and dashes included, over a live preview, and
   the look posts with the report (plateFields → reports.plate_look). The plate page draws the
   newest look for everyone (PlateGallerySlot look=). The characters alone are still the plate:
   JERKY and JER-KY are one plate and one Erk Score.
   Art with an emblem in the split (JAPlate state .emblem — the Texas outline, the PA keystone, …)
   keeps its gap: the lookout only picks which character it follows, and only where the gap can
   stay on the emblem (JAPlate.splits). */
const LOOK_MAX = 8;    // characters, as PlateInput and submit.php
const LOOK_PX = 720;   // preview backing store: 2x the 360px it is shown at
function lookChars(v) { return String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }
function lookOf(stCode, v) {
  return window.JAPlate && window.JAPlate.look ? window.JAPlate.look(stCode, v) : lookChars(v);
}
// Characters before each separator: 'GO 4 IT' → [2, 3].
function lookCuts(s) {
  const out = []; let n = 0;
  for (const ch of String(s || '')) { if (ch === ' ' || ch === '-') out.push(n); else n++; }
  return out;
}
// What a sheet posts for its plate: the bare characters, plus
//   Personalized on → look: the tidied layout ('JERKY', 'GO 4 IT', 'AB CDEF')
//   switched off on the very plate the sheet opened with a look → look: '' (back to the standard)
//   otherwise → no look at all, and the plate keeps whatever it has
function plateFields(value, stCode, personal, business) {
  const plate = lookChars(value);
  if (personal) return { plate, look: lookOf(stCode, value) };
  if (business && business.look && plate === business.name && stCode === business.state) return { plate, look: '' };
  return { plate };
}

function PlateLookPreview({ stateCode, text }) {
  const wrapRef = React.useRef(null);
  const cvRef = React.useRef(null);
  const [res, setRes] = React.useState(0);
  const [drawn, setDrawn] = React.useState(false);
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const measure = (cw) => {
      if (!(cw > 0)) return;
      const want = Math.min(LOOK_PX, Math.round(cw * dpr));
      setRes(prev => (prev && Math.abs(want - prev) / prev < 0.1 ? prev : want));
    };
    const ro = new ResizeObserver(e => measure(e[0].contentRect.width));
    ro.observe(el);
    measure(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  // Each keystroke composes off-screen and only the newest result is copied in, so a slow first
  // compose (sheet and art still loading) can never paint over a later one.
  React.useEffect(() => {
    let live = true;
    if (window.JAPlate && res && stateCode) {
      window.JAPlate.compose({ state: stateCode, number: text, personal: true, width: res })
        .then(r => {
          const cv = cvRef.current;
          if (!live || !cv) return;
          cv.width = r.canvas.width; cv.height = r.canvas.height;
          cv.getContext('2d').drawImage(r.canvas, 0, 0);
          setDrawn(true);
        })
        .catch(() => {});
    }
    return () => { live = false; };
  }, [stateCode, text, res]);
  return (
    <div ref={wrapRef} className="plate-look-view">
      <canvas ref={cvRef} role="img" aria-label={('Preview: ' + (stateCode || '') + ' plate ' + (text || '')).trim()}
        style={{ opacity: drawn ? 1 : 0 }} />
    </div>
  );
}

const LOOK_MARKS = [['none', 'None'], ['space', 'Space'], ['dash', 'Dash']];
function PlateEntry({ value, onChange, stCode, onState, personal, onPersonal }) {
  const J = window.JAPlate;
  const st = J && stCode ? J.find(stCode) : null;
  const emblem = !!(st && st.emblem);
  const chars = lookChars(value);
  const n = chars.length;
  const shown = personal ? lookOf(stCode, value) : chars;      // exactly what the preview draws
  const cuts = lookCuts(shown);
  const seps = shown.replace(/[A-Z0-9]/g, '');
  const kind = !seps ? 'none' : (/^ +$/.test(seps) ? 'space' : (/^-+$/.test(seps) ? 'dash' : ''));
  const at = cuts.length === 1 ? cuts[0] : 0;
  const ok = st && J.splits ? J.splits(st, chars) : [];        // where a single split may sit
  const aim = st && st.sepAfter > 0 ? st.sepAfter : Math.floor(n / 2);
  const home = ok.reduce((b, k) => (Math.abs(k - aim) < Math.abs(b - aim) ? k : b), ok[0] || 1);
  const place = (k, sep) => onChange(chars.slice(0, k) + sep + chars.slice(k));
  // Typing with Personalized on: A–Z 0–9 and single separators, never leading, at most two, and at
  // most LOOK_MAX characters. On emblem art a dash is the gap too, and a newly typed separator
  // moves the gap rather than adding a second one.
  const typed = (v) => {
    let s = String(v).toUpperCase().replace(/[^A-Z0-9 -]/g, '').replace(/^[ -]+/, '').replace(/([ -])[ -]+/g, '$1');
    let c = 0; s = s.replace(/[A-Z0-9]/g, ch => (++c > LOOK_MAX ? '' : ch));
    if (emblem) {
      s = s.replace(/-/g, ' ');
      const now = lookCuts(s);
      if (now.length > 1) {
        const was = lookCuts(value);
        const fresh = now.find(k => was.indexOf(k) < 0);
        const k = fresh != null ? fresh : now[now.length - 1], a = s.replace(/ /g, '');
        s = a.slice(0, k) + ' ' + a.slice(k);
      }
    } else {
      let k = 0; s = s.replace(/[ -]/g, ch => (++k > 2 ? '' : ch));
    }
    onChange(s);
  };
  const setMark = (m) => {
    if (m === 'none') return onChange(chars);
    const sep = m === 'dash' ? '-' : ' ';
    if (cuts.length) return onChange(shown.replace(/[ -]/g, sep));
    place(home, sep);
  };
  const i = ok.indexOf(at);
  const move = (d) => { const k = ok[i + d]; if (k) place(k, emblem ? ' ' : (seps[0] || ' ')); };
  const toggle = () => {
    if (personal) { onChange(chars); onPersonal(false); }
    else onPersonal(true);
  };
  // Emblem art takes one gap only, so switching to it tidies the field to what the preview shows.
  const pickState = (code) => {
    onState(code);
    const next = J && code ? J.find(code) : null;
    if (personal && next && next.emblem) onChange(lookOf(code, value));
  };
  return (
    <div className="col gap-2">
      <div className="flex between center gap-2">
        <label className="ja-meta">LICENSE PLATE</label>
        <button type="button" className={'pill' + (personal ? ' on' : '')} aria-pressed={personal} onClick={toggle}>
          <Icon name="sliders-horizontal" size={15} />Personalized
        </button>
      </div>
      <div className="flex gap-2">
        <PlateInput value={value} onChange={personal ? typed : onChange} maxLength={personal ? LOOK_MAX + 2 : LOOK_MAX} style={{ flex:1 }} />
        <StateSelect value={stCode} onChange={pickState} />
      </div>
      {personal && (
        <div className="plate-look">
          {st && <PlateLookPreview stateCode={stCode} text={shown} />}
          {st && !emblem && (
            <div className="plate-look-row">
              <span className="ja-meta">MARK</span>
              <div className="plate-look-opts" role="group" aria-label="Mark between characters">
                {LOOK_MARKS.map(([m, label]) => (
                  <button key={m} type="button" className={'pill' + (kind === m ? ' on' : '')} aria-pressed={kind === m}
                    disabled={m !== 'none' && n < 2} onClick={() => setMark(m)}>{label}</button>
                ))}
              </div>
            </div>
          )}
          {st && at > 0 && (
            <div className="plate-look-row">
              <span className="ja-meta">{emblem ? 'EMBLEM AFTER' : 'SPLIT AFTER'}</span>
              <div className="plate-look-step">
                <Button type="button" variant="ghost" size="sm" icon="arrow-left" aria-label="Move it one character left"
                  disabled={i <= 0} onClick={() => move(-1)} />
                <span className="plate-look-at" aria-live="polite">{at}</span>
                <Button type="button" variant="ghost" size="sm" icon="arrow-right" aria-label="Move it one character right"
                  disabled={i < 0 || i >= ok.length - 1} onClick={() => move(1)} />
              </div>
            </div>
          )}
          <span className="ja-meta plate-look-hint">
            {!st ? 'Pick the state to see the plate.'
              : emblem ? st.name + ' plate art has an emblem between the characters, so the gap stays on it. Pick which character it follows.'
              : 'Type it the way the plate reads. Spaces and dashes show up where you put them.'}
          </span>
        </div>
      )}
    </div>
  );
}

Object.assign(window, { PlateGallerySlot, PlateEntry, plateFields });


/* ======== website/SiteDetail.jsx ======== */
/* Jerk Alert — Business / jerk detail page */

const TONE = {
  alert:  { accent:'var(--ja-alert)', tint:'var(--ja-alert-tint)', icon:'triangle-alert' },
  heads:  { accent:'var(--ja-orange)', tint:'var(--ja-orange-tint)', icon:'lightbulb' },
  thanks: { accent:'var(--awesome)', tint:'var(--awesome-tint)', icon:'shield-check' },
};

// Lucide fallback for positive/heads-up reasons (no illustrated icon)
const HEADS_LUCIDE = {
  'Thank You':'shield-check', 'Excellent Driving!':'thumbs-up', 'Love the Style':'heart',
  'Expired Tags (Quarterly Reset)':'calendar-clock', 'Broken Light (Quarterly Reset)':'lightbulb',
};

// A reason's glyph: illustrated icon when available, else a tone-colored Lucide icon.
// Illustrated icons fill their padded frame; Lucide falls back to a fixed pixel size.
function ReasonGlyph({ reason, tone, size }) {
  const src = infIconSrc(reason);
  if (src) return <img src={src} alt="" {...infIconAttrs(src)} style={{ width:'100%', height:'100%', objectFit:'contain', display:'block' }} />;
  const t = TONE[tone] || TONE.alert;
  return <Icon name={HEADS_LUCIDE[reason] || t.icon} size={size} color={t.accent} />;
}

function AlertThreadCard({ a }) {
  // A report carries one or more reasons. Seed data and older cached submissions have a
  // single `reason` string instead of a `reasons` array, so normalise both to a list.
  const reasons = (a.reasons && a.reasons.length)
    ? a.reasons
    : [{ reason: a.reason, tone: a.tone, helpful: a.helpful, alertId: a.alertId }];
  const [helpful, setHelpful] = React.useState(a.helpful);
  const [voted, setVoted] = React.useState(!!a.voted);
  const [flagged, setFlagged] = React.useState(!!a.flagged);
  React.useEffect(() => { if (a.voted) setVoted(true); if (a.flagged) setFlagged(true); }, [a.voted, a.flagged]);
  const t = TONE[a.tone] || TONE.alert;
  return (
    /* Stacked rows, not three side-by-side columns: at phone width the old layout squeezed
       the location into a one-word-per-line column and let the meter collide with the
       PENDING badge. Order top to bottom: status row, reason block, footer. */
    <div className="card col gap-3" style={{ padding:16 }}>
      {/* status row — badges left, meter hard right, both free to wrap */}
      <div className="flex between gap-3" style={{ alignItems:'flex-start' }}>
        <span className="flex gap-2 wrapw" style={{ alignItems:'center', minWidth:0 }}>
          {a.verified
            ? <Badge kind="verified" icon="shield-check">VERIFIED</Badge>
            : <Badge kind="outline">UNVERIFIED · COUNTS ¼</Badge>}
          {a.pending && (
            <Badge kind="warn">PENDING · LIVE IN {(window.JA_POST_POLICY && window.JA_POST_POLICY.delayMinutes) || 15} MIN</Badge>
          )}
        </span>
        <span style={{ flex:'none' }}>
          {a.tone === 'alert'
            ? <JerkMeter level={a.level} size={15} />
            : <Icon name={t.icon} size={22} color={t.accent} />}
        </span>
      </div>
      {/* reason block — medallion beside the primary reason, extra reasons as chips below */}
      <div className="flex gap-3" style={{ alignItems:'flex-start' }}>
        <div className="flex center" style={{ width:76, height:76, flex:'none', borderRadius:'var(--r-lg)', padding:12,
          background:t.tint, border:'1.5px solid '+t.accent }}>
          <ReasonGlyph reason={reasons[0].reason} tone={reasons[0].tone} size={36} />
        </div>
        <div className="col grow gap-2" style={{ minWidth:0 }}>
          <span style={{ fontFamily:'var(--font-display)', fontWeight:900, fontSize:19, color:t.accent, lineHeight:1.15 }}>{reasons[0].reason}</span>
          {reasons.length > 1 && (
            <div className="flex gap-2 wrapw" style={{ alignItems:'center' }}>
              {reasons.slice(1).map((r, i) => (
                <span key={i} className="ja-meta flex center gap-1" title={r.reason}
                  style={{ border:'1px solid var(--line-strong)', borderRadius:'var(--r-pill)',
                    padding:'3px 10px 3px 5px', whiteSpace:'nowrap', maxWidth:'100%' }}>
                  {/* illustrated glyphs fill their box, so the chip has to cap the size */}
                  <span className="flex center" style={{ width:18, height:18, flex:'none' }}>
                    <ReasonGlyph reason={r.reason} tone={r.tone} size={14} />
                  </span>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{r.reason}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* footer — where/when on one clamped line, actions hard right */}
      <div className="flex between gap-3" style={{ alignItems:'center', borderTop:'1px solid var(--line)', paddingTop:10 }}>
        <span className="ja-meta flex center gap-1" title={a.where} style={{ minWidth:0 }}>
          <Icon name="map-pin" size={12} color="var(--fg3)" />
          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.where}</span>
          <span style={{ flex:'none', color:'var(--fg3)' }}> · {a.when.toUpperCase()}</span>
        </span>
        <div className="flex center gap-3" style={{ flex:'none' }}>
          <button type="button" className="btn-icon flex center gap-1" onClick={()=>{
              if(voted)return;
              if(!window.JA_USER){window.alert('Sign in to endorse an Alert.');return;}
              setHelpful(h=>h+1);setVoted(true);
              if(a.alertId&&window.JA_vote)window.JA_vote(a.alertId,'helpful').then(function(j){
                if(j&&j.error){setHelpful(h=>h-1);setVoted(false);window.alert(j.error);}
              });
            }}
            aria-label={voted?'You endorsed this report':'Endorse this report'} title={voted?'You endorsed this report':'Endorse this report'} disabled={voted}
            style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:13, color: voted?'var(--ja-teal)':'var(--fg3)' }}>
            <Icon name="thumbs-up" size={15} /> {helpful}
          </button>
          <button type="button" className="btn-icon flex center" aria-label={flagged?'You flagged this report':'Flag this report'} title={flagged?'You flagged this report':'Flag this report'} disabled={flagged} onClick={()=>{
              if(flagged)return;
              if(!window.JA_USER){window.alert('Sign in to flag an Alert.');return;}
              if(!window.confirm('Flag this Alert for review? A moderator looks at it within 5 business days.'))return;
              setFlagged(true);
              if(a.alertId&&window.JA_vote)window.JA_vote(a.alertId,'flag').then(function(j){
                if(j&&j.error){setFlagged(false);window.alert(j.error);return;}
                if(j&&j.counted!==false)window.alert('Flagged. Thanks for keeping the record honest.');
              });
            }}
            style={{ color: flagged?'var(--ja-alert)':'var(--fg3)' }}>
            <Icon name="flag" size={15} />
          </button>
          {/* Block control — required on every card (Apple 1.2 / Play UGC). JA_block drops the
              card, re-reads the feed and returns {error} on failure; the block list is in Settings. */}
          <button type="button" className="btn-icon flex center" aria-label="Block this lookout" title="Block this lookout"
            onClick={()=>{
              if(!a.alertId||!window.JA_block){window.alert('Blocking needs the live Jerk Alert API.');return;}
              if(!window.JA_USER){window.alert('Sign in to block a lookout.');return;}
              if(!window.confirm('Block this lookout? Their Alerts disappear from your feed. You can unblock them in Settings.'))return;
              window.JA_block(a.alertId).then(function(j){if(j&&j.error)window.alert(j.error);});
            }}
            style={{ color:'var(--fg3)' }}>
            <Icon name="user-minus" size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

// The plate page's mini-map: WHERE the most recent Alert was picked, marked with THAT Alert's
// Erk rating (1–5). Until 11 Sep 2026 it drew the state centroid with the plate's overall score,
// which read as a wrong location. No alert with coordinates → state centre, no pin.
function DetailMiniMap({ b, alert }) {
  const elRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const hasPin = !!(alert && alert.lat != null && alert.lng != null);
  const lat = hasPin ? alert.lat : null, lng = hasPin ? alert.lng : null;
  const level = hasPin ? alert.level : null;
  React.useEffect(() => {
    if (!elRef.current || !window.L) return;
    const c = hasPin ? [lat, lng] : ((window.JA_STATE_LATLNG || {})[b.state] || [39.5, -98.35]);
    if (!mapRef.current) {
      // Zoom 16 reads street names and the corner itself; 12 was a whole district.
      mapRef.current = L.map(elRef.current, { zoomControl: false, scrollWheelZoom: false, dragging: false, doubleClickZoom: false }).setView(c, hasPin ? 16 : 7);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OSM' }).addTo(mapRef.current);
      setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80);
    } else {
      mapRef.current.setView(c, hasPin ? 16 : 7);
    }
    if (markerRef.current) { mapRef.current.removeLayer(markerRef.current); markerRef.current = null; }
    if (hasPin) {
      const color = levelColor(level);
      markerRef.current = L.marker(c, { icon: L.divIcon({
        className: '', iconSize: [34, 34], iconAnchor: [17, 17],
        html: '<span style="background:' + color + ';color:#fff;font-family:var(--font-display);font-weight:900;font-size:13px;width:34px;height:34px;border-radius:999px;display:flex;align-items:center;justify-content:center;border:2.5px solid #fff;box-shadow:0 3px 8px rgba(30,45,51,.28)">' + Number(level).toFixed(1) + '</span>'
      }) }).addTo(mapRef.current);
    }
  }, [b.id, hasPin, lat, lng, level]);
  return <div ref={elRef} style={{ height: 220, width: '100%' }} />;
}

function Detail({ b, onBack, onPost, onTip, onTrack, onAward, backLabel, backIcon }) {
  // Was: || Object.values(window.JA_ALERTS)[0] — with the demo set gone that returned undefined
  // and threw on the .filter below, i.e. a white screen on every plate page. An unknown plate is
  // the "Flying Under the Radar" state further down, not a borrowed set of someone else's cards.
  const baseAlerts = window.JA_ALERTS[b.id] || [];
  // A heads-up ('heads') is not a judgement: it must not put a score on the page at all.
  // Only alerts and awards ('thanks') are scoreable, so only they reveal the Erk Meter.
  const scoredCards = baseAlerts.filter(a => a.tone === 'alert' || a.tone === 'thanks');
  const alertCards  = baseAlerts.filter(a => a.tone === 'alert');
  // Every reason across every alert report, for the medallion strip under the meter.
  const flaggedReasons = alertCards.flatMap(a => (a.reasons && a.reasons.length)
    ? a.reasons : [{ reason: a.reason, tone: a.tone }]);
  const scored = scoredCards.length > 0;
  // Newest first, always. The feed arrives newest-first; a card with a timestamp sorts by it, so
  // this device's own just-posted card (ts = now) lands on top. No sort pills on a single plate.
  const alerts = React.useMemo(() => baseAlerts.slice().sort((a,b)=>(b.ts||0)-(a.ts||0)), [baseAlerts]);
  // Most common alert reason (ties broken by recency — first in array wins)
  const topAlert = React.useMemo(() => {
    if (!baseAlerts.length) return null;
    const counts = {};
    baseAlerts.forEach(a => { counts[a.reason] = (counts[a.reason] || 0) + 1; });
    return baseAlerts.reduce((best, a) => counts[a.reason] > counts[best.reason] ? a : best, baseAlerts[0]);
  }, [baseAlerts]);
  const CAT_REASON = { roadrage:'Road Raging', tailgating:'Tailgating', speeding:'Speeding',
    distracted:'Distracted Driving', reckless:'Reckless Driving',
    parking:'Blocked / Took a Parking Spot', rightofway:'Right-of-Way Challenged', headsup:'Excellent Driving!' };
  const cat = (window.JA_CATEGORIES || []).find(c => c.id === b.cat);
  const catColor = cat ? cat.color : '#F97316';
  const infTint = `linear-gradient(135deg, ${catColor}33, ${catColor})`;
  const stateName = (ab) => ({OR:'Oregon',WA:'Washington',CA:'California',NV:'Nevada',ID:'Idaho',AZ:'Arizona',TX:'Texas',NY:'New York',FL:'Florida',CO:'Colorado'}[ab] || ab);
  // Share (30 Sep 2026): the link is jerkalert.org/r/<state>/<plate>. JA_sharePlate (store.js) first
  // posts this plate's lettered image so the chat preview shows the plate, then opens the share
  // sheet — or copies the link where there is no sheet. The button waits on the upload.
  const [sharing, setSharing] = React.useState(false);
  const share = (x) => {
    if (!window.JA_sharePlate) { window.alert('Sharing needs the live Jerk Alert site.'); return; }
    if (sharing) return;
    setSharing(true);
    window.JA_sharePlate(x).then(r => {
      setSharing(false);
      if (!r) return;
      const why = r.card ? '' : (window.JA_USER ? ' (The preview will show the blank plate' + (r.cardError ? ': ' + r.cardError : '') + '.)' : ' (Sign in first and the preview shows the lettered plate.)');
      if (r.copied) window.alert('Link copied. Paste it anywhere — Discord, a text, an email.' + why);
      else if (why) window.alert('Link ready.' + why);
    }, () => setSharing(false));
  };
  const shareLabel = sharing ? 'Preparing card…' : 'Share This Plate';
  const clean = !(window.JA_ALERTS[b.id] && window.JA_ALERTS[b.id].length);
  if (clean) {
    return (
      <main style={{ flex:1, background:'var(--bg-warm)' }}>
        <div className="wrap" style={{ padding:'18px 24px 8px' }}>
          <button type="button" className="navlink flex center gap-1" onClick={onBack} style={{ position:'fixed', top:'calc(80px + env(safe-area-inset-top, 0px))', left:24, zIndex:40, background:'#fff', padding:'8px 14px', borderRadius:'var(--r-pill)', boxShadow:'var(--sh-sm)', border:'1px solid var(--line)' }}>
            <Icon name={backIcon || 'arrow-left'} size={16} /> {backLabel || 'Back'}
          </button>
        </div>
        <div className="wrap" style={{ padding:'8px 24px 0' }}>
          <div className="gallery" style={{ gridTemplateColumns:'5fr 2fr', height:520 }}>
            <PlateGallerySlot stateCode={b.state} number={b.name} look={b.look} />
            <div className="flex center" style={{ background:'linear-gradient(135deg,var(--ja-teal-tint),var(--ja-teal))', padding:24 }}>
              <img src="icons/flying-under-radar.webp" alt="Flying under the radar" width="640" height="540" loading="lazy" style={{ width:'100%', height:'100%', objectFit:'contain', display:'block' }} />
            </div>
          </div>
        </div>
        <div className="wrap" style={{ padding:'28px 24px 64px' }}>
          <div className="detail-grid" style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:32, alignItems:'start' }}>
            <div className="card col center gap-4 pad-card" style={{ padding:'56px 32px', textAlign:'center' }}>
              <div className="flex center" style={{ width:360, maxWidth:'100%' }}>
                <img src="icons/flying-under-radar.webp" alt="Flying under the radar" width="640" height="540" style={{ width:'100%', height:'auto', display:'block' }} />
              </div>
              <div className="col gap-2" style={{ maxWidth:440 }}>
                <div className="flex center gap-3 wrapw" style={{ justifyContent:'center' }}>
                  <span className="plate lg">{b.state} · {b.name}</span>
                </div>
                <h2 className="ja-h2" style={{ margin:'8px 0 0' }}>Flying Under the Radar</h2>
                <p className="ja-body" style={{ color:'var(--fg2)', margin:0 }}>No alerts or awards on this plate yet — a clean record on {stateName(b.state)} roads. Spotted something? Be the first to weigh in.</p>
              </div>
            </div>
            <aside className="col gap-4" style={{ position:'sticky', top:84 }}>
              <div className="card col gap-4" style={{ padding:20 }}>
                <Button variant="primary" size="lg" icon="siren" onClick={()=>onPost(b)} style={{ width:'100%' }}>Post an Alert</Button>
                <Button variant="awesome" size="md" icon="award" style={{ width:'100%' }} onClick={()=>onAward && onAward(b)}>Post an Award</Button>
                <Button variant="secondary" size="md" icon="shield-check" style={{ width:'100%' }} onClick={()=>onTip && onTip(b)}>Friendly Heads Up</Button>
                <Button variant="ghost" size="md" icon="bell" style={{ width:'100%' }} onClick={()=>onTrack && onTrack(b)}>Track This Plate</Button>
                <Button variant="ghost" size="md" icon="share-2" style={{ width:'100%', opacity: sharing ? 0.6 : 1 }} onClick={()=>share(b)}>{shareLabel}</Button>
              </div>
            </aside>
          </div>
        </div>
      </main>
    );
  }
  return (
    <main style={{ flex:1, background:'var(--bg-warm)' }}>
      <div className="wrap" style={{ padding:'18px 24px 8px' }}>
        <button type="button" className="navlink flex center gap-1" onClick={onBack} style={{ position:'fixed', top:'calc(80px + env(safe-area-inset-top, 0px))', left:24, zIndex:40, background:'#fff', padding:'8px 14px', borderRadius:'var(--r-pill)', boxShadow:'var(--sh-sm)', border:'1px solid var(--line)' }}>
          <Icon name={backIcon || 'arrow-left'} size={16} /> {backLabel || 'Back'}
        </button>
      </div>

      {/* plate + top-infraction gallery */}
      <div className="wrap" style={{ padding:'8px 24px 0' }}>
        <div className="gallery" style={{ gridTemplateColumns:'5fr 2fr', height:520 }}>
          <PlateGallerySlot stateCode={b.state} number={b.name} look={b.look} />
          <div style={{ background:'linear-gradient(135deg,#FFE0CC,#F97316)', padding:0, overflow:'hidden', position:'relative' }}>
            {topAlert && (infIconSrc(topAlert.reason)
              ? <img src={infIconSrc(topAlert.reason)} alt={topAlert.reason} {...infIconAttrs(infIconSrc(topAlert.reason), true)}
                  style={{ width:'100%', height:'100%', objectFit:'contain', display:'block', padding:20 }} />
              : <ReasonGlyph reason={topAlert.reason} tone={topAlert.tone} size={220} />
            )}
            {topAlert && (
              <span style={{ position:'absolute', left:16, right:16, bottom:14, textAlign:'center',
                fontFamily:'var(--font-display)', fontWeight:900, fontSize:15, letterSpacing:'.04em',
                textTransform:'uppercase', color:'#fff', textShadow:'0 1px 6px rgba(0,0,0,.35)' }}>
                {topAlert.reason}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* header */}
      <div className="wrap" style={{ padding:'24px 24px 0' }}>
        <div className="flex between wrapw gap-4" style={{ alignItems:'flex-start' }}>
          <div className="col gap-3" style={{ flex:1, minWidth:260 }}>
            <div className="flex center gap-3 wrapw">
              <span className="plate lg">{b.state} · {b.name}</span>
              <StatusBadge status={b.status} />
            </div>
            {scored && <JerkMeter level={b.level} size={24} showScore count={b.alerts} />}
            <div className="flex center gap-3 wrapw">
              <span className="ja-small flex center gap-1" style={{ color:'var(--fg2)' }}><Icon name="map-pin" size={15} color="var(--fg3)" />{b.area}</span>
              <span className="ja-meta">·</span>
              <span className="ja-small" style={{ color:'var(--fg2)' }}>{b.catLabel}</span>
              <span className="ja-meta">·</span>
              <span className="ja-small" style={{ color:'var(--fg2)' }}>Last seen {b.distance.toLowerCase()}</span>
            </div>
            <p className="ja-body" style={{ color:'var(--fg2)', margin:'2px 0', maxWidth:560 }}>{b.about}</p>
          </div>
        </div>
      </div>

      {/* two-col */}
      <div className="wrap" style={{ padding:'28px 24px 64px' }}>
        <div className="detail-grid" style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:32, alignItems:'start' }}>
          {/* main */}
          <div className="col gap-6">
            {/* erk meter — hidden until a scoreable report exists */}
            {!scored && (
              <div className="card col gap-2" style={{ padding:22 }}>
                <h3 className="ja-h3" style={{ margin:0 }}>No Erk Score yet</h3>
                <span className="ja-small" style={{ color:'var(--fg3)', lineHeight:1.5 }}>
                  This plate only has friendly heads-ups. A heads-up isn't a judgement, so it
                  doesn't move the Erk Meter — the score appears once there's an alert or an award.
                </span>
              </div>
            )}
            {scored && (
            <div className="card col gap-4" style={{ padding:22 }}>
              <div className="flex between center">
                <h3 className="ja-h3" style={{ margin:0 }}>Erk Meter</h3>
                <span style={{ fontFamily:'var(--font-display)', fontWeight:900, fontSize:24, color:jerkColor(b.level), lineHeight:1 }}>
                  {b.level.toFixed(1)}<span style={{ fontSize:14, color:'var(--fg3)', fontWeight:700 }}> / 5</span>
                </span>
              </div>
              <div className="flex center" style={{ marginTop:-8 }}>
                <span style={{ fontFamily:'var(--font-display)', fontWeight:900, fontSize:15, letterSpacing:'.05em', textTransform:'uppercase', color:jerkColor(b.level) }}>{jerkTitle(b.level)}</span>
              </div>
              <div style={{ height:18, borderRadius:'var(--r-pill)', background:'var(--jerk-track)', overflow:'hidden' }}>
                <div style={{ height:'100%', width:(b.level/5*100)+'%', background:jerkColor(b.level), borderRadius:'var(--r-pill)', transition:'width .3s' }} />
              </div>
              <span className="ja-small" style={{ color:'var(--fg3)', lineHeight:1.5 }}>
                Weighted score: every plate starts with five 0-erk reports folded in, so one bad
                day can't brand a driver. A single 5-erk alert scores 0.8.
              </span>
              {alertCards.length > 0 && (
                <div className="col gap-2">
                  <span className="ja-meta">FLAGGED FOR · {flaggedReasons.length} REASON{flaggedReasons.length>1?'S':''}</span>
                  <div className="flex center gap-2 wrapw">
                    {flaggedReasons.map((a,i) => {
                      const t = TONE[a.tone] || TONE.alert;
                      return (
                        <span key={i} title={a.reason} className="flex center"
                          style={{ width:44, height:44, borderRadius:'var(--r-md)', flex:'none', padding:7,
                            background:t.tint, border:'1.5px solid '+t.accent }}>
                          <ReasonGlyph reason={a.reason} tone={a.tone} size={22} />
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* alerts */}
            <div className="flex between center" id="alerts" style={{ scrollMarginTop:90 }}>
              <h3 className="ja-h3" style={{ margin:0 }}>{b.alerts} Alert{b.alerts === 1 ? '' : 's'}</h3>
              <Button variant="primary" size="md" icon="siren" onClick={()=>onPost(b)}>Post an Alert</Button>
            </div>
            <div className="col gap-4">
              {alerts.map((a,i) => <AlertThreadCard key={i} a={a} />)}
            </div>
          </div>

          {/* sidebar */}
          <aside className="col gap-4" style={{ position:'sticky', top:84 }}>
            <div className="card col gap-4" style={{ padding:20 }}>
              <Button variant="primary" size="lg" icon="siren" onClick={()=>onPost(b)} style={{ width:'100%' }}>Post an Alert</Button>
              <Button variant="awesome" size="md" icon="award" style={{ width:'100%' }} onClick={()=>onAward && onAward(b)}>Post an Award</Button>
              <Button variant="secondary" size="md" icon="shield-check" style={{ width:'100%' }} onClick={()=>onTip && onTip(b)}>Friendly Heads Up</Button>
              <Button variant="ghost" size="md" icon="bell" style={{ width:'100%' }} onClick={()=>onTrack && onTrack(b)}>Track This Plate</Button>
              <Button variant="ghost" size="md" icon="share-2" style={{ width:'100%', opacity: sharing ? 0.6 : 1 }} onClick={()=>share(b)}>{shareLabel}</Button>
            </div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <DetailMiniMap b={b} alert={alerts.find(a => a.tone === 'alert' && a.lat != null) || null} />
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

Object.assign(window, { Detail });


/* ======== website/SitePostAlert.jsx ======== */
/* Jerk Alert — Post an Alert modal */

/* Hover preview is desktop-only. On touch, a tap fires a synthetic mouseenter and nothing ever
   fires mouseleave, so the preview state would stick — simply don't track it there. */
const CAN_HOVER = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover)').matches;

function PostAlert({ business, onClose, onSubmit, onSignIn }) {
  const MAX_REASONS = 4;
  const [level, setLevel] = React.useState(0);
  const [hover, setHover] = React.useState(0);
  const [reasons, setReasons] = React.useState([]);
  // A plate page with a personalized look opens the sheet with Personalized on and that look typed in.
  const [plate, setPlate] = React.useState(business ? (business.look || business.name) : '');
  const [personal, setPersonal] = React.useState(!!(business && business.look));
  const [stCode, setStCode] = React.useState(business ? business.state : '');
  // Always opens blank (11 Sep 2026) — it used to inherit the plate's area from an earlier report,
  // which showed another lookout's city as if it were this one's. unresolved = free text sitting in
  // the spot field that no suggestion has matched; posting is blocked until it is picked or cleared.
  const [place, setPlace] = React.useState(null);
  const [unresolved, setUnresolved] = React.useState(false);
  const shown = hover || level;
  const labels = ['','Eh, minor','A little jerky','Notable','Pretty bad','Fully Erk\u2019d'];
  const ACCENT = 'var(--ja-alert)', TINT = 'var(--ja-alert-tint)';
  const REASONS = [
    'Road Raging','Tailgating','Speeding','Brake Checking','DUI / DWI','Reckless Driving',
    'Gesturing / Swearing','Threatening to Fight','Aggressive Honking','High Beams',
    'Slowing Down Intentionally','Sus','Left Lane Squatter','Running Red Lights','Ignoring Stop Signs',
    'Threatening Pedestrians & Cyclists','Right-of-Way Challenged','Improper Lane Changes',
    'Merging Dangerously','Failure to Signal','Cutting Off Other Drivers','Distracted Driving',
    'Texting','Blocked / Took a Parking Spot','Blocking Handicapped Access',
  ];
  // Selection is multi-select, capped at MAX_REASONS.
  const atMax = reasons.length >= MAX_REASONS;
  const pick = (r) => {
    setReasons(prev => {
      if (prev.includes(r)) return prev.filter(x => x !== r);  // toggle off
      if (prev.length >= MAX_REASONS) return prev;             // at cap, ignore
      return [...prev, r];                                     // add
    });
  };
  // Cross street is optional, as the picker's label says. submit.php validates plate/state/reasons
  // only, and store.js places a report with no picked place at the plate state's centroid.
  const valid = reasons.length > 0 && level > 0 && /[A-Z0-9]/i.test(plate) && stCode.trim() && !unresolved;
  const submit = () => {
    if (!valid) return;
    onSubmit({ kind: 'alert', ...plateFields(plate, stCode, personal, business), state: stCode.trim().toUpperCase(),
      where: place ? place.label : '', lat: place ? place.lat : null, lng: place ? place.lng : null,
      level, reasons, photo: null });
  };

  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal modal-flex" onClick={e=>e.stopPropagation()}>
        {/* header */}
        <div className="modal-head flex between center" style={{ padding:'20px 24px', borderBottom:'1px solid var(--line)' }}>
          <div className="flex center gap-2">
            <Icon name="siren" size={22} color="var(--ja-alert)" />
            <h3 className="ja-h3" style={{ margin:0 }}>Post an Alert</h3>
          </div>
          <button type="button" className="btn-icon modal-close" aria-label="Close" onClick={onClose}><Icon name="x" size={22} /></button>
        </div>

        <div className="modal-body">
        {!(window.JA_USER && window.JA_USER.verified) && (
          <div className="col gap-2" style={{ margin:'16px 24px 0', padding:'14px 16px', borderRadius:'var(--r-lg)',
            background:'var(--ja-yellow-tint)', border:'1.5px solid var(--ja-yellow-deep)' }}>
            <span className="flex center gap-2" style={{ fontFamily:'var(--font-display)', fontWeight:900, fontSize:14,
              letterSpacing:'.05em', textTransform:'uppercase', color:'var(--ja-ink)' }}>
              <Icon name="shield-alert" size={17} color="var(--ja-yellow-deep)" style={{ flex:'none' }} />
              You are posting as an unverified lookout
            </span>
            <span className="ja-small" style={{ color:'var(--fg2)', lineHeight:1.5 }}>
              Your alert goes live in {(window.JA_POST_POLICY && window.JA_POST_POLICY.delayMinutes) || 15} minutes
              and counts a quarter as much as a verified lookout's. Verify your phone once and every alert you post
              goes up instantly at full weight.
            </span>
            {onSignIn && (
              <span className="ja-meta" style={{ cursor:'pointer', color:'var(--ja-teal)', minHeight:44, display:'flex', alignItems:'center' }}
                onClick={onSignIn}>Verify your number →</span>
            )}
          </div>
        )}
        <div className="col gap-5" style={{ padding:24 }}>
          {/* plate — PlateEntry (SitePlateDisplay.jsx): PlateInput + StateSelect + the Personalized look */}
          <PlateEntry value={plate} onChange={setPlate} stCode={stCode} onState={setStCode}
            personal={personal} onPersonal={setPersonal} />

          {/* where — selected from the streets database */}
          <div className="col gap-2">
            <label className="ja-meta">WHERE IT HAPPENED</label>
            <PlacePicker stateCode={stCode} place={place} onPick={setPlace} onUnresolved={setUnresolved} placeholder="Cross street or exit — optional" />
          </div>

          {/* level */}
          <div className="col gap-2">
            <label className="ja-meta">ERK RATING</label>
            <div className="flex center gap-3" style={{ flexWrap:'wrap' }}>
              <span className="meter" style={{ gap:0 }} onMouseLeave={CAN_HOVER ? ()=>setHover(0) : undefined}>
                {[1,2,3,4,5].map(i => (
                  <span key={i} role="button" aria-label={labels[i]}
                    style={{ cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', minWidth:'var(--tap-min, 48px)', minHeight:'var(--tap-min, 48px)' }}
                    onMouseEnter={CAN_HOVER ? ()=>setHover(i) : undefined} onClick={()=>setLevel(i)}>
                    <Icon name="triangle-alert" size={34} sw={2.2}
                      style={{ color: i <= shown ? levelColor(shown) : 'var(--jerk-track)', transition:'color .12s' }} />
                  </span>
                ))}
              </span>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:15, color: shown?levelColor(shown):'var(--fg3)' }}>
                {shown ? labels[shown] : 'Tap to rate'}
              </span>
            </div>
          </div>

          {/* what happened — icon buttons */}
          <div className="col gap-3">
            <div className="flex between center">
              <label className="ja-meta">WHAT HAPPENED? <span style={{ color:'var(--fg3)', fontWeight:600 }}>(pick up to {MAX_REASONS})</span></label>
              {reasons.length > 0 && (
                <span className="ja-meta" style={{ color: atMax ? 'var(--ja-alert)' : 'var(--fg3)' }}>{reasons.length} / {MAX_REASONS} selected</span>
              )}
            </div>
            <div className="reason-grid">
              {REASONS.map((label) => {
                const on = reasons.includes(label);
                const blocked = !on && atMax;
                const src = infIconSrc(label);
                return (
                  <button key={label} type="button"
                    className={'reason-btn'+(on?' on':'')+(blocked?' disabled':'')}
                    onClick={()=>pick(label)}
                    style={ on ? { borderColor:ACCENT, background:TINT, color:ACCENT } : null }>
                    {src
                      ? <img src={src} alt="" {...infIconAttrs(src)} />
                      : <span className="rlucide" style={{ color:ACCENT }}><Icon name="triangle-alert" size={28} /></span>}
                    <span className="rlabel" style={ on ? { color:ACCENT } : null }>{label}</span>
                  </button>
                );
              })}
            </div>
            <span className="ja-meta" style={{ color:'var(--fg3)' }}>Pick up to {MAX_REASONS} that fit. We keep it to the list so it stays fair — we warn, we don't pile on.</span>
          </div>

          {/* Structured-only reporting: fixed list + level + location. No free text, no photos. */}
          <span className="ja-meta flex center gap-2" style={{ color:'var(--fg3)', lineHeight:1.5 }}>
            <Icon name="shield" size={15} color="var(--ja-teal)" style={{ flex:'none' }} />
            Reports use the list above only — no free text or uploads, so no one can be defamed or doxxed.
          </span>
        </div>

        </div>

        {/* footer */}
        <div className="modal-foot flex between center wrapw gap-3" style={{ padding:'16px 24px', borderTop:'1px solid var(--line)', background:'var(--bg-warm)' }}>
          {(() => {
            // Verified posts go up immediately at full weight; unverified ones wait and count a
            // quarter. Kept honest and visible right next to the Post button.
            const v = !!(window.JA_USER && window.JA_USER.verified);
            const mins = (window.JA_POST_POLICY && window.JA_POST_POLICY.delayMinutes) || 15;
            return (
              <span className="ja-meta flex center gap-1">
                <Icon name="shield" size={14} color={v ? 'var(--ja-teal)' : 'var(--ja-yellow-deep)'} />
                {v ? 'Posted as VERIFIED LOOKOUT' : 'POSTING UNVERIFIED · LIVE IN ' + mins + ' MIN'}
              </span>
            );
          })()}
          <div className="flex gap-2" style={{ marginLeft:'auto' }}>
            <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="md" icon="siren"
              onClick={submit} disabled={!valid}>
              Post Alert
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { PostAlert });


/* ======== website/SiteTipHat.jsx ======== */
/* Jerk Alert — Tip Your Hat modal (Friendly Heads Up) */

function TipHat({ business, onClose, onSubmit }) {
  const MAX_REASONS = 4;
  const [reasons, setReasons] = React.useState([]);
  // A plate page with a personalized look opens the sheet with Personalized on and that look typed in.
  const [plate, setPlate] = React.useState(business ? (business.look || business.name) : '');
  const [personal, setPersonal] = React.useState(!!(business && business.look));
  const [stCode, setStCode] = React.useState(business ? business.state : '');
  const [place, setPlace] = React.useState(null);           // always blank (11 Sep 2026)
  const [unresolved, setUnresolved] = React.useState(false); // free text in the spot field blocks posting
  const ACCENT = 'var(--ja-orange)', TINT = 'var(--ja-orange-tint)';
  const REASONS = [
    'Expired Tags (Quarterly Reset)',
    'Broken Light (Quarterly Reset)',
  ];
  const atMax = reasons.length >= MAX_REASONS;
  const pick = (r) => {
    setReasons(prev => {
      if (prev.includes(r)) return prev.filter(x => x !== r);  // toggle off
      if (prev.length >= MAX_REASONS) return prev;             // at cap, ignore
      return [...prev, r];                                     // add
    });
  };
  // Cross street is optional, as the picker's label says (same rule as SitePostAlert).
  const valid = reasons.length > 0 && /[A-Z0-9]/i.test(plate) && stCode.trim() && !unresolved;
  const submit = () => {
    if (!valid) return;
    onSubmit({ kind: 'tip', ...plateFields(plate, stCode, personal, business), state: stCode.trim().toUpperCase(),
      where: place ? place.label : '', lat: place ? place.lat : null, lng: place ? place.lng : null,
      level: 1, reasons, photo: null });
  };

  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal modal-flex" onClick={e=>e.stopPropagation()}>
        {/* header */}
        <div className="modal-head flex between center" style={{ padding:'20px 24px', borderBottom:'1px solid var(--line)' }}>
          <div className="flex center gap-2">
            <Icon name="lightbulb" size={22} color="var(--ja-orange)" />
            <h3 className="ja-h3" style={{ margin:0 }}>Friendly Heads Up</h3>
          </div>
          <button type="button" className="btn-icon modal-close" aria-label="Close" onClick={onClose}><Icon name="x" size={22} /></button>
        </div>

        <div className="modal-body col gap-5" style={{ padding:24 }}>
          {/* plate — PlateEntry (SitePlateDisplay.jsx): PlateInput + StateSelect + the Personalized look */}
          <PlateEntry value={plate} onChange={setPlate} stCode={stCode} onState={setStCode}
            personal={personal} onPersonal={setPersonal} />

          {/* where — selected from the streets database */}
          <div className="col gap-2">
            <label className="ja-meta">WHERE IT HAPPENED</label>
            <PlacePicker stateCode={stCode} place={place} onPick={setPlace} onUnresolved={setUnresolved} placeholder="Cross street or exit — optional" />
          </div>

          {/* friendly heads up — icon buttons */}
          <div className="col gap-3">
            <div className="flex between center">
              <label className="ja-meta">FRIENDLY HEADS UP <span style={{ color:'var(--fg3)', fontWeight:600 }}>(pick up to {MAX_REASONS})</span></label>
              {reasons.length > 0 && (
                <span className="ja-meta" style={{ color: atMax ? 'var(--ja-orange)' : 'var(--fg3)' }}>{reasons.length} / {MAX_REASONS} selected</span>
              )}
            </div>
            <div className="reason-grid" style={{ gridTemplateColumns:'repeat(2,1fr)' }}>
              {REASONS.map((label) => {
                const on = reasons.includes(label);
                const blocked = !on && atMax;
                const src = infIconSrc(label);
                return (
                  <button key={label} type="button"
                    className={'reason-btn'+(on?' on':'')+(blocked?' disabled':'')}
                    onClick={()=>pick(label)}
                    style={ on ? { borderColor:ACCENT, background:TINT, color:ACCENT } : null }>
                    {src
                      ? <img src={src} alt="" {...infIconAttrs(src)} />
                      : <span className="rlucide" style={{ color:ACCENT }}><Icon name="lightbulb" size={28} /></span>}
                    <span className="rlabel" style={ on ? { color:ACCENT } : null }>{label}</span>
                  </button>
                );
              })}
            </div>
            <span className="ja-meta" style={{ color:'var(--fg3)' }}>A gentle nudge, not an alert — let a driver know about something worth a quick fix.</span>
          </div>
        </div>

        {/* footer */}
        <div className="modal-foot flex between center wrapw gap-3" style={{ padding:'16px 24px', borderTop:'1px solid var(--line)', background:'var(--bg-warm)' }}>
          {(() => {
            // Verified posts go up immediately at full weight; unverified ones wait and count a
            // quarter. Kept honest and visible right next to the Post button.
            const v = !!(window.JA_USER && window.JA_USER.verified);
            const mins = (window.JA_POST_POLICY && window.JA_POST_POLICY.delayMinutes) || 15;
            return (
              <span className="ja-meta flex center gap-1">
                <Icon name="shield" size={14} color={v ? 'var(--ja-teal)' : 'var(--ja-yellow-deep)'} />
                {v ? 'Posted as VERIFIED LOOKOUT' : 'POSTING UNVERIFIED · LIVE IN ' + mins + ' MIN'}
              </span>
            );
          })()}
          <div className="flex gap-2" style={{ marginLeft:'auto' }}>
            <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="md" icon="bell"
              onClick={submit} disabled={!valid}>
              Send Heads Up
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { TipHat });


/* ======== website/SiteAward.jsx ======== */
/* Jerk Alert — Post an Award modal (positive shout-outs) */

function PostAward({ business, onClose, onSubmit }) {
  const MAX_REASONS = 4;
  const [reasons, setReasons] = React.useState([]);
  // A plate page with a personalized look opens the sheet with Personalized on and that look typed in.
  const [plate, setPlate] = React.useState(business ? (business.look || business.name) : '');
  const [personal, setPersonal] = React.useState(!!(business && business.look));
  const [stCode, setStCode] = React.useState(business ? business.state : '');
  const [place, setPlace] = React.useState(null);           // always blank (11 Sep 2026)
  const [unresolved, setUnresolved] = React.useState(false); // free text in the spot field blocks posting
  const ACCENT = 'var(--awesome)', TINT = 'var(--awesome-tint)';
  const REASONS = [
    'Thank You',
    'Excellent Driving!',
    'Love the Style',
    'Avoided Accident',
    'Cat-like Reflexes',
    'Defensive Driving Champion',
    'Road Ninja',
    'Better Late Than Never',
  ];
  const atMax = reasons.length >= MAX_REASONS;
  const pick = (r) => {
    setReasons(prev => {
      if (prev.includes(r)) return prev.filter(x => x !== r);  // toggle off
      if (prev.length >= MAX_REASONS) return prev;             // at cap, ignore
      return [...prev, r];                                     // add
    });
  };
  // Cross street is optional, as the picker's label says (same rule as SitePostAlert).
  const valid = reasons.length > 0 && /[A-Z0-9]/i.test(plate) && stCode.trim() && !unresolved;
  const submit = () => {
    if (!valid) return;
    onSubmit({ kind: 'award', ...plateFields(plate, stCode, personal, business), state: stCode.trim().toUpperCase(),
      where: place ? place.label : '', lat: place ? place.lat : null, lng: place ? place.lng : null,
      level: 1, reasons, photo: null });
  };

  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal modal-flex" onClick={e=>e.stopPropagation()}>
        {/* header */}
        <div className="modal-head flex between center" style={{ padding:'20px 24px', borderBottom:'1px solid var(--line)' }}>
          <div className="flex center gap-2">
            <Icon name="award" size={22} color="var(--awesome)" />
            <h3 className="ja-h3" style={{ margin:0 }}>Post an Award</h3>
          </div>
          <button type="button" className="btn-icon modal-close" aria-label="Close" onClick={onClose}><Icon name="x" size={22} /></button>
        </div>

        <div className="modal-body col gap-5" style={{ padding:24 }}>
          {/* plate — PlateEntry (SitePlateDisplay.jsx): PlateInput + StateSelect + the Personalized look */}
          <PlateEntry value={plate} onChange={setPlate} stCode={stCode} onState={setStCode}
            personal={personal} onPersonal={setPersonal} />

          {/* where — selected from the streets database */}
          <div className="col gap-2">
            <label className="ja-meta">WHERE IT HAPPENED</label>
            <PlacePicker stateCode={stCode} place={place} onPick={setPlace} onUnresolved={setUnresolved} placeholder="Cross street or exit — optional" />
          </div>

          {/* awards — icon buttons */}
          <div className="col gap-3">
            <div className="flex between center">
              <label className="ja-meta">GIVE AN AWARD <span style={{ color:'var(--fg3)', fontWeight:600 }}>(pick up to {MAX_REASONS})</span></label>
              {reasons.length > 0 && (
                <span className="ja-meta" style={{ color: atMax ? 'var(--awesome)' : 'var(--fg3)' }}>{reasons.length} / {MAX_REASONS} selected</span>
              )}
            </div>
            <div className="reason-grid" style={{ gridTemplateColumns:'repeat(2,1fr)' }}>
              {REASONS.map((label) => {
                const on = reasons.includes(label);
                const blocked = !on && atMax;
                const src = infIconSrc(label);
                return (
                  <button key={label} type="button"
                    className={'reason-btn'+(on?' on':'')+(blocked?' disabled':'')}
                    onClick={()=>pick(label)}
                    style={ on ? { borderColor:ACCENT, background:TINT, color:ACCENT } : null }>
                    {src
                      ? <img src={src} alt="" {...infIconAttrs(src)} />
                      : <span className="rlucide" style={{ color:ACCENT }}><Icon name="award" size={28} /></span>}
                    <span className="rlabel" style={ on ? { color:ACCENT } : null }>{label}</span>
                  </button>
                );
              })}
            </div>
            <span className="ja-meta" style={{ color:'var(--fg3)' }}>Caught someone driving right? Hand them an award — good road karma travels fast.</span>
          </div>
        </div>

        {/* footer */}
        <div className="modal-foot flex between center wrapw gap-3" style={{ padding:'16px 24px', borderTop:'1px solid var(--line)', background:'var(--bg-warm)' }}>
          {(() => {
            // Verified posts go up immediately at full weight; unverified ones wait and count a
            // quarter. Kept honest and visible right next to the Post button.
            const v = !!(window.JA_USER && window.JA_USER.verified);
            const mins = (window.JA_POST_POLICY && window.JA_POST_POLICY.delayMinutes) || 15;
            return (
              <span className="ja-meta flex center gap-1">
                <Icon name="shield" size={14} color={v ? 'var(--ja-teal)' : 'var(--ja-yellow-deep)'} />
                {v ? 'Posted as VERIFIED LOOKOUT' : 'POSTING UNVERIFIED · LIVE IN ' + mins + ' MIN'}
              </span>
            );
          })()}
          <div className="flex gap-2" style={{ marginLeft:'auto' }}>
            <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
            <Button variant="awesome" size="md" icon="award"
              onClick={submit} disabled={!valid}>
              Post Award
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { PostAward });


/* ======== website/SiteTrackPlate.jsx ======== */
/* Jerk Alert \u2014 TrackPlate modal: email subscription, double opt-in via api/track.php */

function TrackPlate({ business, onClose, onSubmit }) {
  const [plate, setPlate] = React.useState(business ? business.name : '');
  const [stCode, setStCode] = React.useState(business ? business.state : '');
  const [email, setEmail] = React.useState('');
  const [freq, setFreq] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const FREQS = [
    { id:'realtime', label:'Real-Time', icon:'zap',        desc:'The moment it happens' },
    { id:'daily',    label:'Daily',     icon:'calendar',   desc:'Morning digest' },
    { id:'weekly',   label:'Weekly',    icon:'calendar-days', desc:'Sunday summary' },
  ];

  const valid = email.includes('@') && email.includes('.') && freq !== null
    && plate.trim().length > 0 && stCode.trim().length === 2;

  const start = () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    const req = window.JA_trackPlate
      ? window.JA_trackPlate({ plate: plate.trim().toUpperCase(), state: stCode.trim().toUpperCase(),
          email: email.trim(), freq })
      : Promise.resolve({ ok: true, confirmed: true });
    req.then(j => {
      setBusy(false);
      if (!j || j.error) { setErr((j && j.error) || 'Something went wrong. Try again.'); return; }
      onSubmit && onSubmit(j.confirmed
        ? 'You\u2019re now tracking this plate!'
        : 'Almost there \u2014 check your email to confirm.');
    });
  };

  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal modal-flex" onClick={e => e.stopPropagation()}>

        <div className="modal-head flex between center" style={{ padding:'20px 24px', borderBottom:'1px solid var(--line)' }}>
          <div className="flex center gap-2">
            <Icon name="bell" size={22} color="var(--ja-teal)" />
            <h3 className="ja-h3" style={{ margin:0 }}>Track This Plate</h3>
          </div>
          <button type="button" className="btn-icon modal-close" aria-label="Close" onClick={onClose}><Icon name="x" size={22} /></button>
        </div>

        <div className="modal-body col gap-5" style={{ padding:24 }}>

          <div className="col gap-2">
            <label className="ja-meta">LICENSE PLATE</label>
            <div className="flex gap-2">
              <PlateInput value={plate} onChange={setPlate} style={{ flex:1 }} />
              <StateSelect value={stCode} onChange={setStCode} />
            </div>
          </div>

          <div className="col gap-2">
            <label className="ja-meta">YOUR EMAIL</label>
            <div style={{ position:'relative' }}>
              <span style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:'var(--ja-teal)', display:'flex' }}>
                <Icon name="mail" size={18} />
              </span>
              <input className="field" type="email" placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="done"
                style={{ paddingLeft:42 }} />
            </div>
            <span className="ja-meta" style={{ color:'var(--fg3)' }}>
              We send one confirmation email first. Nothing else until you click it.
            </span>
          </div>

          <div className="col gap-3">
            <label className="ja-meta">HOW OFTEN?</label>
            {/* Same tile as the reason picker, teal accent — one selection idiom across every sheet. */}
            <div className="reason-grid track-freq-grid" style={{ gridTemplateColumns:'repeat(3,1fr)' }}>
              {FREQS.map(f => {
                const on = freq === f.id;
                return (
                  <button key={f.id} type="button" className={'reason-btn compact'+(on?' on':'')}
                    onClick={() => setFreq(f.id)}
                    style={ on ? { borderColor:'var(--ja-teal)', background:'var(--ja-teal-tint)', color:'var(--ja-teal)' } : null }>
                    <Icon name={f.icon} size={22} color={on ? 'var(--ja-teal)' : 'var(--fg3)'} />
                    <span className="rlabel" style={{ fontSize:13, color: on ? 'var(--ja-teal)' : 'var(--fg1)' }}>{f.label}</span>
                    <span className="rdesc">{f.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {err && (
            <div className="flex center gap-2" style={{ padding:'10px 12px', borderRadius:'var(--r-md)',
              background:'var(--ja-alert-tint)', border:'1px solid var(--ja-alert)' }}>
              <Icon name="triangle-alert" size={16} color="var(--ja-alert-ink)" />
              <span style={{ fontSize:13, color:'var(--ja-alert-ink)', fontWeight:600 }}>{err}</span>
            </div>
          )}

        </div>

        <div className="modal-foot flex between center wrapw gap-3" style={{ padding:'16px 24px', borderTop:'1px solid var(--line)', background:'var(--bg-warm)' }}>
          <span className="ja-meta flex center gap-1">
            <Icon name="shield" size={14} color="var(--ja-teal)" />
            Unsubscribe any time
          </span>
          <div className="flex gap-2" style={{ marginLeft:'auto' }}>
            <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="md" icon="bell"
              onClick={start} disabled={!valid || busy}>
              {busy ? 'Sending\u2026' : 'Start Tracking'}
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
}

Object.assign(window, { TrackPlate });


/* ======== website/SiteAuth.jsx ======== */
/* Jerk Alert — Sign in / verify modal (phone OTP → Verified Lookout) */

function AuthModal({ onClose, onDone }) {
  const [step, setStep] = React.useState('phone');
  // autoFocus alone is not enough on the code step: the input mounts after the network call
  // (outside the tap that started it) and Firebase's reCAPTCHA hands focus back to the page
  // afterwards. Focus explicitly, and retry twice while nothing else has taken the focus.
  const codeRef = React.useRef(null);
  React.useEffect(() => {
    if (step !== 'code') return;
    const grab = () => {
      const el = codeRef.current, ae = document.activeElement;
      if (el && (!ae || ae === document.body || ae === el)) el.focus();
    };
    grab();
    const t1 = setTimeout(grab, 150), t2 = setTimeout(grab, 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [step]);
  const [phone, setPhone] = React.useState('');
  const [code, setCode] = React.useState('');
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [dev, setDev] = React.useState(false);
  const digits = phone.replace(/\D/g, '');
  const phoneOk = digits.length >= 10;

  // One last silent attempt before we ask anyone for a code: Firebase may still hold this
  // device's credential even though our session lapsed. If it works the sheet closes itself.
  React.useEffect(() => {
    let dead = false;
    if (window.JA_trySilentSignIn) {
      window.JA_trySilentSignIn().then(u => { if (u && !dead) { onDone && onDone(); } });
    }
    return () => { dead = true; };
  }, []);

  const send = () => {
    if (!phoneOk || busy) return;
    setErr(''); setBusy(true);
    window.JA_startAuth(phone).then(j => {
      setBusy(false);
      if (j && j.ok) { setDev(!!j.dev); setStep('code'); }
      else setErr((j && j.error) || 'Could not send code.');
    }).catch(() => { setBusy(false); setErr('Network error — try again.'); });
  };
  const verify = () => {
    if (code.replace(/\D/g, '').length < 6 || busy) return;
    setErr(''); setBusy(true);
    window.JA_verifyAuth(phone, code).then(j => {
      setBusy(false);
      if (j && j.ok) { onDone && onDone(); }
      else setErr((j && j.error) || 'Invalid code.');
    }).catch(() => { setBusy(false); setErr('Network error — try again.'); });
  };

  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal modal-flex" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-head flex between center" style={{ padding: '20px 24px', borderBottom: '1px solid var(--line)' }}>
          <div className="flex center gap-2">
            <Icon name="shield-check" size={22} color="var(--ja-teal)" />
            <h3 className="ja-h3" style={{ margin: 0 }}>Become a Verified Lookout</h3>
          </div>
          <button type="button" className="btn-icon modal-close" aria-label="Close" onClick={onClose}><Icon name="x" size={22} /></button>
        </div>

        <div className="modal-body col gap-5" style={{ padding: 24 }}>
          <p className="ja-body" style={{ margin: 0, color: 'var(--fg2)' }}>
            We verify every lookout by phone. It keeps reports fair — one real person, one account — and your number is <strong>never shown publicly</strong>.
          </p>

          {step === 'phone' ? (
            <div className="col gap-2">
              <label className="ja-meta">MOBILE NUMBER</label>
              <input className="field" inputMode="tel" placeholder="(555) 555-5555" value={phone}
                onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} autoFocus />
            </div>
          ) : (
            <div className="col gap-2">
              <label className="ja-meta">
                ENTER THE 6-DIGIT CODE {dev && <span style={{ color: 'var(--fg3)', fontWeight: 600 }}>· preview mode: any code works</span>}
              </label>
              <input ref={codeRef} className="field plate-field" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="––––––" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={e => e.key === 'Enter' && verify()} autoFocus />
              <span className="ja-meta" style={{ cursor: 'pointer', color: 'var(--ja-teal)' }}
                onClick={() => { setStep('phone'); setCode(''); setErr(''); }}>← Use a different number</span>
            </div>
          )}

          {err && <span className="ja-meta" style={{ color: 'var(--ja-alert)' }}>{err}</span>}
        </div>

        <div className="modal-foot flex between center wrapw gap-3" style={{ padding: '16px 24px', borderTop: '1px solid var(--line)', background: 'var(--bg-warm)' }}>
          <span className="ja-meta flex center gap-1"><Icon name="lock" size={14} color="var(--ja-teal)" />Private & never shared</span>
          <div className="flex gap-2" style={{ marginLeft:'auto' }}>
            <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
            {step === 'phone'
              ? <Button variant="primary" size="md" icon="arrow-right" onClick={send}
                  disabled={!phoneOk || busy}>Send Code</Button>
              : <Button variant="primary" size="md" icon="shield-check" onClick={verify}
                  disabled={busy}>Verify</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { AuthModal });


/* ======== website/SiteNotifications.jsx ======== */
/* Jerk Alert — the "You" tab: notifications inbox + tracked plates */

const JA_NOTE_TYPES = {
  nearby:   { icon:'siren',     accent:'var(--ja-alert)',  tint:'var(--ja-alert-tint)' },
  endorsed: { icon:'thumbs-up', accent:'var(--awesome)',   tint:'var(--awesome-tint)' },
  hidden:   { icon:'eye-off',   accent:'var(--ja-orange)', tint:'var(--ja-orange-tint)' },
  system:   { icon:'info',      accent:'var(--ja-teal)',   tint:'var(--ja-teal-tint)' },
};

function jaTimeAgo(ts) {
  const s = Math.max(0, (Date.now() - (ts || 0)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  if (s < 604800) return Math.floor(s / 86400) + 'd ago';
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const JA_FREQ_LABEL = { realtime: 'Real-time', daily: 'Daily digest', weekly: 'Weekly summary' };

function NotificationRow({ n, onOpen }) {
  const t = JA_NOTE_TYPES[n.type] || JA_NOTE_TYPES.system;
  const clickable = !!n.plate;
  return (
    <div className="card flex gap-3" onClick={() => clickable && onOpen(n.plate, n.state)}
      style={{ padding:16, alignItems:'flex-start', cursor: clickable ? 'pointer' : 'default' }}>
      {/* Unread = a filled medallion; read = an outlined one. Same 44px tile as the plate page's reason chips. */}
      <span className="flex center" style={{ width:44, height:44, flex:'none', padding:9,
        borderRadius:'var(--r-md)', background: n.read ? '#fff' : t.tint, border:'1.5px solid ' + (n.read ? 'var(--line-strong)' : t.accent) }}>
        <Icon name={t.icon} size={20} color={t.accent} />
      </span>
      <div className="col grow gap-1" style={{ minWidth:0 }}>
        <span className="ja-h4" style={{ fontSize:16, lineHeight:1.25, fontWeight: n.read ? 700 : 800 }}>{n.title}</span>
        <span className="ja-small" style={{ color:'var(--fg2)' }}>{n.body}</span>
        <span className="ja-meta">{jaTimeAgo(n.ts)}{n.plate ? ' · ' + n.state + ' · ' + n.plate : ''}</span>
      </div>
      {clickable && <Icon name="chevron-right" size={18} color="var(--fg3)" />}
    </div>
  );
}

function WatchRow({ w, onStop }) {
  return (
    <div className="card flex between center gap-3" style={{ padding:16 }}>
      <div className="col gap-2" style={{ minWidth:0 }}>
        <div className="flex center gap-2 wrapw">
          <span className="plate">{w.state} · {w.plate}</span>
          {!w.confirmed && (
            <Badge kind="warn">CONFIRM IN YOUR EMAIL</Badge>
          )}
        </div>
        <span className="ja-meta">{(JA_FREQ_LABEL[w.freq] || w.freq).toUpperCase()} · {w.email}</span>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onStop(w.id)}>Unwatch</Button>
    </div>
  );
}

/* ── "Your alerts": the reports you posted ─────────────────────────────────────────────
   Same vocabulary as the rest of the tab: the plate chip, the Erk Meter, the tone accents
   from SiteDetail's TONE map (repeated here so the app's ESM copy needs no cross-file
   import), jaTimeAgo + ja-meta, and a pill shaped like WatchRow's CONFIRM IN YOUR EMAIL.
   A live report gets no pill — a pill on the normal case is noise. */
const JA_MY_TONE = {
  alert:  { accent:'var(--ja-alert)',  tint:'var(--ja-alert-tint)' },
  heads:  { accent:'var(--ja-orange)', tint:'var(--ja-orange-tint)' },
  thanks: { accent:'var(--awesome)',   tint:'var(--awesome-tint)' },
};
const JA_MY_STATUS = {
  pending: { label:'PENDING',            kind:'warn' },
  partial: { label:'A CARD WAS REMOVED', kind:'warn' },
  removed: { label:'REMOVED',            kind:'outline' },
};

function MyReportRow({ r, onOpen, onDelete }) {
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const tone = (r.alerts && r.alerts.length && r.alerts[0].tone)
    || (r.kind === 'award' ? 'thanks' : r.kind === 'tip' ? 'heads' : 'alert');
  const t = JA_MY_TONE[tone] || JA_MY_TONE.alert;
  const st = JA_MY_STATUS[r.status];
  const cards = r.alerts || [];

  const remove = () => {
    setBusy(true); setErr(null);
    Promise.resolve(onDelete(r.id)).then(res => {
      if (res && res.error) { setBusy(false); setConfirming(false); setErr(res.error); }
    });
  };

  return (
    <div className="card col gap-3" style={{ padding:16, opacity: r.status === 'removed' ? .7 : 1 }}>
      <div className="flex between center gap-3" onClick={() => onOpen(r.plate, r.state)}
        style={{ cursor:'pointer' }}>
        <div className="flex center gap-2 wrapw" style={{ minWidth:0 }}>
          <span className="plate">{r.state} · {r.plate}</span>
          {tone === 'alert' && <JerkMeter level={r.level} size={13} />}
          {st && <Badge kind={st.kind}>{st.label}</Badge>}
        </div>
        <Icon name="chevron-right" size={18} color="var(--fg3)" />
      </div>
      {cards.length > 0 && (
        <div className="flex gap-2 wrapw">
          {cards.map(a => (
            <span key={a.id} className="ja-meta" style={{ padding:'3px 10px',
              borderRadius:'var(--r-pill)', border:'1px solid ' + (a.hidden ? 'var(--line-strong)' : t.accent),
              background: a.hidden ? 'var(--bg-warm)' : t.tint,
              color: a.hidden ? 'var(--fg3)' : 'var(--fg1)',
              textDecoration: a.hidden ? 'line-through' : 'none' }}>{a.reason}</span>
          ))}
        </div>
      )}
      <div className="flex between center gap-3 wrapw">
        <span className="ja-meta">
          {jaTimeAgo(r.ts)}{r.where ? ' · ' + r.where : ''}
          {r.status === 'pending' ? ' · PUBLISHES IN A FEW MINUTES' : ''}
        </span>
        {confirming ? (
          <div className="flex center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy}>Keep</Button>
            <Button variant="primary" size="sm" onClick={remove} disabled={busy}>
              {busy ? 'Removing…' : 'Remove Report'}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" icon="trash-2" onClick={() => setConfirming(true)}>Remove</Button>
        )}
      </div>
      {confirming && !err && (
        <span className="ja-meta" style={{ color:'var(--fg2)' }}>
          This removes the report and its endorsements from Jerk Alert and changes this plate's
          Erk Score. Moderators keep a copy for a while, in case the tap was a mistake.
        </span>
      )}
      {err && <span className="ja-meta" style={{ color:'var(--ja-alert)' }}>{err}</span>}
    </div>
  );
}

function NotificationsView({ onOpenPlate, onSignIn, onAccount }) {
  const user = window.JA_USER;
  const [, force] = React.useReducer(x => x + 1, 0);
  const [showAllMine, setShowAllMine] = React.useState(false);
  const n = window.JA_NOTIFICATIONS || { unread:0, items:[], watches:[], loaded:false };
  const m = window.JA_MY_REPORTS || { items:[], loaded:false, error:null };
  const mine = showAllMine ? m.items : m.items.slice(0, 10);

  React.useEffect(() => {
    const h = () => force();
    window.addEventListener('ja:notify', h);
    return () => window.removeEventListener('ja:notify', h);
  }, []);

  React.useEffect(() => {
    if (user && window.JA_loadNotifications) window.JA_loadNotifications();
    if (user && window.JA_loadMyReports) window.JA_loadMyReports();
  }, [!!user]);

  // Opening the tab is the read receipt.
  React.useEffect(() => {
    if (user && n.unread > 0 && window.JA_markNotificationsRead) {
      const t = setTimeout(() => window.JA_markNotificationsRead('all'), 900);
      return () => clearTimeout(t);
    }
  }, [user, n.unread]);

  const stop = (id) => { if (window.JA_untrackWatch) window.JA_untrackWatch(id); };
  const removeReport = (id) => window.JA_deleteMyReport
    ? window.JA_deleteMyReport(id)
    : Promise.resolve({ error: 'Deleting needs the Jerk Alert API.' });

  return (
    <main style={{ flex:1, background:'var(--bg-warm)' }}>
      <div className="wrap" style={{ padding:'32px 24px 72px', maxWidth:760 }}>
        <div className="flex between gap-3" style={{ marginBottom:24, alignItems:'flex-start' }}>
          <div className="col gap-2" style={{ minWidth:0 }}>
          <h2 className="ja-h2" style={{ margin:0 }}>You</h2>
          <p className="ja-body" style={{ margin:0, color:'var(--fg2)' }}>
            Alerts near you, lookouts backing up your reports, and the plates you're watching.
          </p>
        </div>
          {/* The header's ME avatar is gone (11 Sep 2026); sign-out / Settings live here now. */}
          {user && <Button variant="ghost" size="md" icon="sliders-horizontal" onClick={onAccount} style={{ flex:'none' }}>Settings</Button>}
        </div>

        {!user ? (
          <div className="card col center gap-4" style={{ padding:'48px 32px', textAlign:'center' }}>
            <span className="flex center" style={{ width:64, height:64, borderRadius:'var(--r-pill)',
              background:'var(--ja-teal-tint)', border:'1.5px solid var(--ja-teal)' }}>
              <Icon name="shield-check" size={30} color="var(--ja-teal)" />
            </span>
            <div className="col gap-2" style={{ maxWidth:400 }}>
              <h3 className="ja-h3" style={{ margin:0 }}>Sign in to see your activity</h3>
              <p className="ja-body" style={{ margin:0, color:'var(--fg2)' }}>
                Verified lookouts get alerts near them, a heads up when someone backs up a report, and a list of the plates they track.
              </p>
            </div>
            <Button variant="primary" size="md" icon="shield-check" onClick={onSignIn}>Become a Verified Lookout</Button>
          </div>
        ) : (
          <div className="col gap-6">
            <div className="col gap-4">
              <div className="flex between center">
                <h3 className="ja-h3" style={{ margin:0 }}>Activity</h3>
                {n.unread > 0 && (
                  <Badge kind="new">{n.unread} NEW</Badge>
                )}
              </div>
              {!n.loaded ? (
                <span className="ja-meta">Loading your activity…</span>
              ) : n.error ? (
                <div className="card col center gap-3" style={{ padding:'44px 28px', textAlign:'center' }}>
                  <span className="flex center" style={{ width:56, height:56, borderRadius:'var(--r-pill)',
                    background:'var(--ja-alert-tint)', border:'1.5px solid var(--ja-alert)' }}>
                    <Icon name="triangle-alert" size={26} color="var(--ja-alert)" />
                  </span>
                  <div className="col gap-1" style={{ maxWidth:380 }}>
                    <span className="ja-h4" style={{ fontSize:16 }}>Couldn't load your activity</span>
                    <span className="ja-small" style={{ color:'var(--fg2)' }}>{n.error}</span>
                  </div>
                  <Button variant="ghost" size="sm" icon="rotate-ccw"
                    onClick={() => window.JA_loadNotifications && window.JA_loadNotifications()}>Try Again</Button>
                </div>
              ) : n.items.length === 0 ? (
                <div className="card col center gap-3" style={{ padding:'44px 28px', textAlign:'center' }}>
                  <span className="flex center" style={{ width:56, height:56, borderRadius:'var(--r-pill)',
                    background:'var(--bg-warm)', border:'1.5px solid var(--line-strong)' }}>
                    <Icon name="bell" size={26} color="var(--fg3)" />
                  </span>
                  <div className="col gap-1" style={{ maxWidth:380 }}>
                    <span className="ja-h4" style={{ fontSize:16 }}>All quiet out there</span>
                    <span className="ja-small" style={{ color:'var(--fg2)' }}>
                      When a lookout posts near you — or backs up one of your reports — it lands here.
                    </span>
                  </div>
                </div>
              ) : (
                <div className="col gap-3">
                  {n.items.map(item => <NotificationRow key={item.id} n={item} onOpen={onOpenPlate} />)}
                </div>
              )}
            </div>

            <div className="col gap-4">
              <h3 className="ja-h3" style={{ margin:0 }}>Your alerts</h3>
              {!m.loaded ? (
                <span className="ja-meta">Loading your alerts…</span>
              ) : m.error ? (
                <div className="card col center gap-3" style={{ padding:'44px 28px', textAlign:'center' }}>
                  <span className="flex center" style={{ width:56, height:56, borderRadius:'var(--r-pill)',
                    background:'var(--ja-alert-tint)', border:'1.5px solid var(--ja-alert)' }}>
                    <Icon name="triangle-alert" size={26} color="var(--ja-alert)" />
                  </span>
                  <div className="col gap-1" style={{ maxWidth:380 }}>
                    <span className="ja-h4" style={{ fontSize:16 }}>Couldn't load your alerts</span>
                    <span className="ja-small" style={{ color:'var(--fg2)' }}>{m.error}</span>
                  </div>
                  <Button variant="ghost" size="sm" icon="rotate-ccw"
                    onClick={() => window.JA_loadMyReports && window.JA_loadMyReports()}>Try Again</Button>
                </div>
              ) : m.items.length === 0 ? (
                <div className="card col center gap-3" style={{ padding:'44px 28px', textAlign:'center' }}>
                  <span className="flex center" style={{ width:56, height:56, borderRadius:'var(--r-pill)',
                    background:'var(--bg-warm)', border:'1.5px solid var(--line-strong)' }}>
                    <Icon name="siren" size={26} color="var(--fg3)" />
                  </span>
                  <div className="col gap-1" style={{ maxWidth:380 }}>
                    <span className="ja-h4" style={{ fontSize:16 }}>You haven't posted an alert yet</span>
                  </div>
                </div>
              ) : (
                <div className="col gap-3">
                  {mine.map(r => (
                    <MyReportRow key={r.id} r={r} onOpen={onOpenPlate} onDelete={removeReport} />
                  ))}
                  {!showAllMine && m.items.length > mine.length && (
                    <Button variant="ghost" size="sm" onClick={() => setShowAllMine(true)}>
                      Show More ({m.items.length - mine.length})
                    </Button>
                  )}
                </div>
              )}
            </div>

            {n.watches.length > 0 && (
              <div className="col gap-4">
                <h3 className="ja-h3" style={{ margin:0 }}>Plates you're watching</h3>
                <div className="col gap-3">
                  {n.watches.map(w => <WatchRow key={w.id} w={w} onStop={stop} />)}
                </div>
                <span className="ja-meta" style={{ color:'var(--fg3)' }}>
                  Every tracking email also carries a one-click unsubscribe link.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

Object.assign(window, { NotificationsView });


/* ======== website/SiteMobileBar.jsx ======== */
/* Jerk Alert — mobile bottom tab bar (phones only; CSS-gated ≤700px) */

function MobileBar({ view, user, unread, onHome, onWatch, onPost, onYou, onAccount, hidden }) {
  if (hidden) return null;
  const count = unread > 9 ? '9+' : unread;
  return (
    <nav className="mobilebar" role="navigation" aria-label="Primary">
      <button className={'mb-item' + (view === 'home' ? ' on' : '')} onClick={onHome} aria-label="Home">
        <Icon name="house" size={21} sw={2.2} /><span>Home</span>
      </button>
      <button className={'mb-item' + (view === 'results' ? ' on' : '')} onClick={onWatch} aria-label="The Watch">
        <Icon name="bell" size={21} sw={2.2} /><span>The Watch</span>
      </button>
      <button className="mb-fab" onClick={onPost} aria-label="Post an Alert">
        <Icon name="siren" size={25} sw={2.2} />
      </button>
      <button className={'mb-item' + (view === 'you' ? ' on' : '')} onClick={onYou} aria-label="Your activity">
        <span className="mb-badge-wrap">
          <Icon name="inbox" size={21} sw={2.2} />
          {unread > 0 && <span className="mb-badge">{count}</span>}
        </span>
        <span>You</span>
      </button>
      <button className="mb-item" onClick={onAccount} aria-label={user ? 'Account' : 'Sign in'}>
        {user
          ? <Avatar init="ME" size={22} />
          : <Icon name="shield-check" size={21} sw={2.2} />}
        <span>{user ? 'Account' : 'Sign In'}</span>
      </button>
    </nav>
  );
}

/* Account sheet — the mobile bar's Account button used to sign out on a single tap. Signing
   back in costs a real SMS, so the destructive action now sits behind a confirmation. */
function AccountSheet({ user, onClose, onSignOut, onYou }) {
  if (!user) return null;
  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal modal-flex" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-head flex between center" style={{ padding: '20px 24px', borderBottom: '1px solid var(--line)' }}>
          <div className="flex center gap-2">
            <Icon name="shield-check" size={22} color="var(--ja-teal)" />
            <h3 className="ja-h3" style={{ margin: 0 }}>Sign out?</h3>
          </div>
          <button type="button" className="btn-icon modal-close" aria-label="Close" onClick={onClose}><Icon name="x" size={22} /></button>
        </div>

        <div className="modal-body col gap-4" style={{ padding: 24 }}>
          <div className="flex center gap-3">
            <Avatar init="ME" size={44} />
            <div className="col" style={{ gap: 2 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 17 }}>{user.display}</span>
              <span className="ja-meta flex center gap-1" style={{ color: 'var(--ja-teal)' }}>
                <Icon name="shield-check" size={13} />Verified Lookout
              </span>
            </div>
          </div>
          <p className="ja-body" style={{ margin: 0, color: 'var(--fg2)' }}>
            Signing out ends this session on this device. Your reports and tracked plates stay put — but getting back in needs a fresh code by text.
          </p>
          <span className="ja-meta" style={{ color: 'var(--fg3)' }}>
            Blocked lookouts and notification settings live in the Jerk Alert app. To delete your account from the web, use <a href="legal/delete-account.html" style={{ color: 'var(--ja-teal)' }}>jerkalert.org/legal/delete-account</a>.
          </span>
        </div>

        <div className="modal-foot flex between center" style={{ padding: '16px 24px', borderTop: '1px solid var(--line)', background: 'var(--bg-warm)' }}>
          <Button variant="ghost" size="md" onClick={onClose}>Stay signed in</Button>
          <Button variant="primary" size="md" icon="log-out" onClick={onSignOut}>Sign Out</Button>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { MobileBar, AccountSheet });


/* ======== website/SiteSettings.jsx ======== */
/* Jerk Alert — website Settings sheet (11 Sep 2026): who you are, blocked lookouts, legal,
   delete account, sign out. The app has its own module version (_app/src/components/
   SiteSettings.jsx) with the push-radius row on top; this one has no push, so no radius.
   API calls go through store.js (JA_listBlocks / JA_unblock / JA_deleteAccount → jaFetch). */

function SettingsRow({ icon, title, sub, children, onClick, danger }) {
  return (
    <div className="flex gap-3" onClick={onClick}
      style={{ alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid var(--line)',
        minHeight: 44, cursor: onClick ? 'pointer' : 'default' }}>
      <Icon name={icon} size={19} color={danger ? 'var(--ja-alert)' : 'var(--ja-teal)'} style={{ marginTop: 2, flex: 'none' }} />
      <div className="col gap-1" style={{ flex: 1, minWidth: 0 }}>
        <span className="ja-h4" style={{ fontSize: 16, color: danger ? 'var(--ja-alert)' : 'var(--fg1)' }}>{title}</span>
        {sub && <span className="ja-small" style={{ color: 'var(--fg2)' }}>{sub}</span>}
        {children}
      </div>
      {onClick && <Icon name="chevron-right" size={18} color="var(--fg3)" style={{ marginTop: 4, flex: 'none' }} />}
    </div>
  );
}

function Settings({ onClose, onSignOut }) {
  const user = window.JA_USER;
  const [blocks, setBlocks] = React.useState(null);        // null = loading
  const [stage, setStage] = React.useState('main');        // main | delete | deleting | done
  const [mode, setMode] = React.useState('anonymize');
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    if (user && window.JA_listBlocks) window.JA_listBlocks().then(setBlocks); else setBlocks([]);
  }, [user]);

  const unblock = (id) => {
    if (!window.JA_unblock) return;
    window.JA_unblock(id).then(j => {
      if (j && j.error) { window.alert(j.error); return; }
      setBlocks(b => (b || []).filter(x => x.blocked_id !== id));
    });
  };
  const confirmDelete = () => {
    setStage('deleting'); setErr('');
    if (!window.JA_deleteAccount) { setStage('delete'); setErr('Deleting needs the live Jerk Alert API.'); return; }
    window.JA_deleteAccount(mode).then(j => {
      if (j && j.ok) setStage('done');
      else { setStage('delete'); setErr((j && j.error) || 'Could not delete the account.'); }
    });
  };

  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal modal-flex" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-head flex between center" style={{ padding: '20px 24px', borderBottom: '1px solid var(--line)' }}>
          <h3 className="ja-h3" style={{ margin: 0 }}>Settings</h3>
          <button type="button" className="btn-icon modal-close" aria-label="Close" onClick={onClose}><Icon name="x" size={22} /></button>
        </div>

        <div className="modal-body">
        {stage === 'main' && (
          <div style={{ padding: '8px 24px 4px' }}>
            <SettingsRow icon="hard-hat" title={user ? user.display : 'Not signed in'}
              sub={user ? 'Verified Lookout · your number is never shown' : 'Sign in to post, vote, and get alerts'} />

            <SettingsRow icon="user-minus" title="Blocked lookouts"
              sub={blocks === null ? 'Loading…' : blocks.length ? 'Their reports are hidden from your feed.' : 'You haven\u2019t blocked anyone.'}>
              {blocks && blocks.length > 0 && (
                <div className="col gap-2" style={{ marginTop: 8 }}>
                  {blocks.map(b => (
                    <div key={b.blocked_id} className="flex between center gap-3"
                      style={{ padding: '8px 12px', background: 'var(--bg-warm)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
                      <span className="ja-small" style={{ color: 'var(--fg1)' }}>{b.display}</span>
                      <Button variant="ghost" size="sm" onClick={() => unblock(b.blocked_id)}>Unblock</Button>
                    </div>
                  ))}
                </div>
              )}
            </SettingsRow>

            <SettingsRow icon="file-text" title="Privacy Policy" onClick={() => window.open('legal/privacy.html', '_blank')} />
            <SettingsRow icon="scale" title="Terms of Service" onClick={() => window.open('legal/terms.html', '_blank')} />

            {user && (
              <React.Fragment>
                <SettingsRow icon="trash-2" title="Delete account" danger
                  sub="Permanent. Removes your number, name, and votes." onClick={() => setStage('delete')} />
                <div style={{ padding: '16px 0 20px' }}>
                  <Button variant="ghost" size="md" icon="log-out" style={{ width: '100%' }}
                    onClick={() => { if (window.confirm('Sign out? Getting back in takes a fresh code by text.')) onSignOut(); }}>Sign out</Button>
                </div>
              </React.Fragment>
            )}
          </div>
        )}

        {(stage === 'delete' || stage === 'deleting') && (
          <div className="col gap-5" style={{ padding: 24 }}>
            <p className="ja-body" style={{ margin: 0 }}>
              This deletes your account, phone number, display name, notification tokens, and votes. It cannot be undone.
            </p>
            <div className="col gap-2">
              <label className="ja-meta">WHAT SHOULD HAPPEN TO YOUR ALERTS?</label>
              {[['anonymize', 'Keep them, without you', 'The Alerts stay as a community record with no author attached.'],
                ['purge', 'Delete everything', 'Your Alerts, Awards, and votes go too.']].map(([v, t, s]) => (
                <label key={v} className="flex gap-3" style={{ alignItems: 'flex-start', padding: '12px 14px', minHeight: 44,
                  border: '1.5px solid ' + (mode === v ? 'var(--ja-alert)' : 'var(--line-strong)'), borderRadius: 'var(--r-md)', cursor: 'pointer' }}>
                  <input type="radio" name="delmode" checked={mode === v} onChange={() => setMode(v)}
                    style={{ width: 18, height: 18, marginTop: 3, accentColor: 'var(--ja-alert)', flex: 'none' }} />
                  <span className="col gap-1">
                    <span className="ja-h4" style={{ fontSize: 15.5 }}>{t}</span>
                    <span className="ja-small" style={{ color: 'var(--fg2)' }}>{s}</span>
                  </span>
                </label>
              ))}
            </div>
            {err && <span className="ja-meta" style={{ color: 'var(--ja-alert)' }}>{err}</span>}
            <div className="flex gap-2 between">
              <Button variant="ghost" size="md" onClick={() => setStage('main')}>Back</Button>
              <Button variant="primary" size="md" icon="trash-2" onClick={confirmDelete}
                style={{ opacity: stage === 'deleting' ? .55 : 1, pointerEvents: stage === 'deleting' ? 'none' : 'auto' }}>
                {stage === 'deleting' ? 'Deleting\u2026' : 'Delete my account'}
              </Button>
            </div>
          </div>
        )}

        {stage === 'done' && (
          <div className="col center gap-4" style={{ padding: 32, textAlign: 'center' }}>
            <img src="assets/logo-mark.webp" alt="" style={{ width: 96, height: 'auto' }} />
            <h4 className="ja-h3" style={{ margin: 0 }}>Account deleted.</h4>
            <p className="ja-body" style={{ margin: 0, color: 'var(--fg2)' }}>
              You're signed out everywhere. Drive safe out there.
            </p>
            <Button variant="primary" size="md" onClick={onClose}>Done</Button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { Settings });


/* ======== website/app.jsx ======== */
/* Jerk Alert — app shell + routing */

/* Error boundary — 14 Aug 2026. A render crash (nearly always a field a bad API response did
   not send) unmounted the whole tree and left a white page, which is the least useful thing a
   tester can send us. This keeps the page up, says what happened in plain words, and hands
   over a copyable block so a bug report carries the actual error text. Audit item 2.4. */
function ErrorReport({ error, stack, onRetry, retryLabel }) {
  const [copied, setCopied] = React.useState(false);
  const preRef = React.useRef(null);
  const details = [
    'Jerk Alert — error report',
    'when:      ' + new Date().toISOString(),
    'url:       ' + location.href,
    'signed in: ' + (window.JA_USER ? 'yes' : 'no'),
    'error:     ' + ((error && (error.message || String(error))) || 'unknown'),
    (stack || '').split('\n').slice(0, 6).join('\n').trim(),
    'agent:     ' + navigator.userAgent,
  ].filter(Boolean).join('\n');
  const copy = () => {
    // No clipboard API on an http origin or inside some WebViews — select the block instead so
    // a long-press copy still works.
    const select = () => {
      const el = preRef.current;
      if (!el || !window.getSelection) return;
      const r = document.createRange();
      r.selectNodeContents(el);
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(r);
    };
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2500); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(details).then(done, select);
    else select();
  };
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-warm)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }}>
      <div className="card" role="alert" style={{ width: '100%', maxWidth: 560, padding: '28px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p className="ja-eyebrow" style={{ margin: 0 }}>Something broke</p>
        <h1 className="ja-h3" style={{ margin: 0 }}>This screen didn’t load.</h1>
        <p className="ja-body" style={{ margin: 0, color: 'var(--fg2)', textWrap: 'pretty' }}>Not your fault, and nothing you posted was lost. Try again, or reload the page. If you can, copy the details below into your report — it tells us exactly what failed.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {onRetry && <button type="button" className="btn btn-lg btn-primary" onClick={onRetry}>{retryLabel || 'Try again'}</button>}
          <button type="button" className="btn btn-lg btn-ghost" onClick={() => location.reload()}>Reload</button>
        </div>
        <pre ref={preRef} className="ja-meta" style={{ margin: 0, padding: '12px 14px', background: 'var(--bg-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', color: 'var(--fg2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>{details}</pre>
        <button type="button" className="btn btn-md btn-ghost" onClick={copy} style={{ alignSelf: 'flex-start', minHeight: 44 }}>{copied ? 'Copied' : 'Copy details'}</button>
      </div>
    </div>
  );
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stack: null, attempt: 0 };
    this.retry = this.retry.bind(this);
  }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    this.setState({ stack: (error && error.stack) || (info && info.componentStack) || '' });
    console.error('[JA] screen crashed', error, info && info.componentStack);
  }
  // Bumping the key remounts App, so "Try again" starts from a clean home screen rather than
  // re-rendering the same broken state and tripping the boundary again immediately.
  retry() { this.setState((s) => ({ error: null, stack: null, attempt: s.attempt + 1 })); }
  render() {
    if (this.state.error) return <ErrorReport error={this.state.error} stack={this.state.stack} onRetry={this.retry} retryLabel="Back to plate search" />;
    return <React.Fragment key={this.state.attempt}>{this.props.children}</React.Fragment>;
  }
}

function App() {
  const [view, setView] = React.useState('home'); // home | results | detail | you
  const [query, setQuery] = React.useState('');
  const [sortMode, setSortMode] = React.useState('relevance');
  const [biz, setBiz] = React.useState(null);
  const [posting, setPosting] = React.useState(false);
  const [tipping, setTipping] = React.useState(false);
  const [tracking, setTracking] = React.useState(false);
  const [awarding, setAwarding] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [authOpen, setAuthOpen] = React.useState(false);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const pendingRef = React.useRef(null);
  // Where the detail page was opened from, so its back link returns there rather than
  // always dumping the visitor on a results list they never saw.
  const fromRef = React.useRef('results');
  const [, force] = React.useReducer(x => x + 1, 0);

  React.useEffect(() => {
    if (window.JA_resolveLocation) window.JA_resolveLocation();
    const h = () => force();
    window.addEventListener('ja:update', h);
    window.addEventListener('ja:auth', h);
    window.addEventListener('ja:notify', h);
    // Posting throttles (once per 15 min per connection, once per plate per day, five per plate
    // per day) are rejected by the server after the optimistic update — show why.
    const pe = (e) => { setToast({ msg: e.detail || 'That post was not accepted.', error: true }); setTimeout(() => setToast(null), 4200); };
    window.addEventListener('ja:posterror', pe);
    return () => { window.removeEventListener('ja:update', h); window.removeEventListener('ja:auth', h); window.removeEventListener('ja:notify', h); window.removeEventListener('ja:posterror', pe); };
  }, []);

  const go = (v) => { setView(v); window.scrollTo(0,0); };
  // Footer links into a home-page section: land on home, then scroll once it has rendered.
  const goSection = (id) => { go('home'); setTimeout(() => { const el = document.getElementById(id); if (el) window.scrollTo({ top: window.pageYOffset + el.getBoundingClientRect().top - 80, behavior: 'smooth' }); }, 80); };
  const unread = (window.JA_NOTIFICATIONS && window.JA_NOTIFICATIONS.unread) || 0;
  const search = (q, sort) => { setQuery(q || ''); setSortMode(sort || 'relevance'); go('results'); };
  const openBiz = (b) => { if (view !== 'detail') fromRef.current = view; setBiz(b); go('detail'); };
  const openCat = (c) => { setQuery(c.label); setSortMode('relevance'); go('results'); };
  const plateLookup = (plate, st) => {
    const q = (plate || '').trim().toUpperCase();
    if (!q) { search(''); return; }
    const open = () => {
      const match = window.JA_BUSINESSES.find(x => x.name === q && (!st || x.state === st));
      if (match) { openBiz(match); return; }
      openBiz({ id:'lookup-'+(st||'')+'-'+q, state:st||'NM', name:q, area:'', level:0, alerts:0,
        status:'clean', cat:'', catLabel:'', distance:'', about:'', photos:false });
    };
    // Ask the server first: the in-memory list is only the recent feed.
    if (window.JA_lookupPlate) window.JA_lookupPlate(q, st).then(open, open); else open();
  };
  // Deep link from the /states pages: ?plate=ABC123&state=ME#alerts opens that plate and
  // lands on its alert list. Poll for the section: the lookup resolves over the network.
  React.useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const plate = (qs.get('plate') || '').trim();
    if (!plate) return;
    plateLookup(plate, (qs.get('state') || '').trim().toUpperCase());
    if (window.location.hash !== '#alerts') return;
    let tries = 0;
    const iv = setInterval(() => {
      const el = document.getElementById('alerts');
      if (el) { clearInterval(iv); window.scrollTo({ top: window.pageYOffset + el.getBoundingClientRect().top - 90, behavior:'smooth' }); }
      else if (++tries > 24) clearInterval(iv);
    }, 150);
    return () => clearInterval(iv);
  }, []);

  // Reporting is open to unverified lookouts while we build the crew — the sheet says so
  // plainly, the post waits 15 minutes and counts a quarter. Tracking still needs an account,
  // because it sends email.
  const post = (b) => setPosting(true);
  const tip = (b) => setTipping(true);
  const track = (b) => requireAuth(() => setTracking(true));
  const award = (b) => setAwarding(true);
  const submitTrack = (msg) => {
    setTracking(false);
    setToast({ msg: msg || 'You\u2019re now tracking this plate!' });
    setTimeout(()=>setToast(null), 3200);
  };
  const requireAuth = (fn) => { if (window.JA_USER) fn(); else { pendingRef.current = fn; setAuthOpen(true); } };
  const authDone = () => { setAuthOpen(false); const fn = pendingRef.current; pendingRef.current = null; if (fn) fn(); };
  const submit = (rec) => {
    setPosting(false);
    if (rec && window.JA_addSubmission) window.JA_addSubmission(rec);
    // Land on the plate you just reported: the card, the new score and the other sightings
    // are the point of posting, and it saves searching for your own plate to check it took.
    if (rec && rec.plate) plateLookup(rec.plate, rec.state);
    setToast({ msg: window.JA_USER && window.JA_USER.verified
      ? 'Alert posted! The crew thanks you.'
      : 'Alert received — it goes live in ' + ((window.JA_POST_POLICY && window.JA_POST_POLICY.delayMinutes) || 15) + ' minutes.' });
    setTimeout(()=>setToast(null), 3200);
  };
  const submitTip = (rec) => {
    setTipping(false);
    if (rec && window.JA_addSubmission) window.JA_addSubmission(rec);
    setToast({ msg: 'Heads-up sent! Good road karma to you.' });
    setTimeout(()=>setToast(null), 3200);
  };
  const submitAward = (rec) => {
    setAwarding(false);
    if (rec && window.JA_addSubmission) window.JA_addSubmission(rec);
    setToast({ msg: 'Award posted! Good road karma to you.' });
    setTimeout(()=>setToast(null), 3200);
  };

  return (
    <React.Fragment>
      <TopNav onHome={()=>go('home')} onSearch={search} onPlateLookup={plateLookup} onPost={()=>post()}
        onWatch={()=>search('')} onYou={()=>go('you')} unread={unread}
        user={window.JA_USER} onSignIn={()=>setAuthOpen(true)} />
      {view === 'home' && <Home onSearch={search} onPlateLookup={plateLookup} onOpenBusiness={openBiz} onOpenCategory={openCat} onPost={()=>post()} />}
      {view === 'results' && <Results query={query} initialSort={sortMode} onOpenBusiness={openBiz} onPost={()=>post()} />}
      {view === 'detail' && biz && <Detail b={biz} onBack={()=>go(fromRef.current==='home'?'home':'results')}
        backLabel="Back" backIcon="arrow-left"
        onPost={post} onTip={tip} onTrack={track} onAward={award} />}
      {view === 'you' && <NotificationsView onOpenPlate={plateLookup} onSignIn={()=>setAuthOpen(true)} onAccount={()=>setAccountOpen(true)} />}
      <Footer onWatch={()=>search('')} onPost={()=>post()} onSignIn={()=>setAuthOpen(true)} onSection={goSection} />
      <MobileBar view={view} user={window.JA_USER} unread={unread}
        onHome={()=>go('home')} onWatch={()=>search('')} onPost={()=>post()} onYou={()=>go('you')}
        onAccount={()=>window.JA_USER ? setAccountOpen(true) : setAuthOpen(true)}
        hidden={posting || tipping || awarding || tracking || authOpen || accountOpen} />
      {posting && <PostAlert business={biz} onClose={()=>setPosting(false)} onSignIn={()=>{ setPosting(false); setAuthOpen(true); }} onSubmit={submit} />}
      {tipping && <TipHat business={biz} onClose={()=>setTipping(false)} onSubmit={submitTip} />}
      {awarding && <PostAward business={biz} onClose={()=>setAwarding(false)} onSubmit={submitAward} />}
      {tracking && <TrackPlate business={biz} onClose={()=>setTracking(false)} onSubmit={submitTrack} />}
      {authOpen && <AuthModal onClose={()=>setAuthOpen(false)} onDone={authDone} />}
      {accountOpen && <Settings onClose={()=>setAccountOpen(false)}
        onSignOut={()=>{ setAccountOpen(false); if (window.JA_logout) window.JA_logout(); }} />}
      {toast && <div className={'toast' + (toast.error ? ' toast-error' : '')}><Icon name={toast.error ? 'triangle-alert' : 'check-circle'} size={18} color={toast.error ? '#fff' : 'var(--ja-yellow)'} />{toast.msg}</div>}
    </React.Fragment>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<AppErrorBoundary><App /></AppErrorBoundary>);


/* The page checks this to decide whether the dev fallback is needed. Last line on purpose:
   if anything above threw, it stays unset. */
window.JA_SITE_BUNDLE = 'bundle';
console.log('[JA] UI: app.bundle.js  build 2026-10-17  ja-site-sources: 492818a9383e99ab');
